import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources - we'll start with inline JSON and later move to separate files
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: "Home",
        standings: "Standings",
        gamePeriods: "Game Periods",
        stats: "Stats",
        playoffs: "Playoffs",
        finals: "Finals",
        champions: "Champions",
        formerChampions: "Former Champions",
        teams: "Teams",
        players: "Players",
        rules: "Rules",
        about: "About",
        teamOwners: "Team Owners",
        apply: "Apply Now",
        login: "Login",
        register: "Register",
        settings: "Settings",
        logout: "Logout",
        admin: "Admin",
        league: "League",
        news: "News",
        info: "Info",
        ownerNews: "Owner News",
        tradeNews: "Trade News",
        analytics: "Analytics"
      },

      // Game Periods page
      gamePeriods: {
        title: "Game Periods",
        subtitle: "Games played per team in {{season}} — only teams with a GM.",
        running: "running",
        upcoming: "upcoming",
        finished: "finished",
        daysLeft: "{{days}} days left",
        minGames: "Minimum games",
        maxGames: "Maximum games",
        cumulativeHint: "Counted cumulatively: all regular season games played up to the end of this period.",
        noPeriods: "No game periods defined for the active season.",
        noActiveSeason: "No active season.",
        noTeams: "No teams with a GM.",
        legend: {
          belowMin: "Minimum not reached",
          minReached: "Minimum reached",
          complete: "All games played"
        },
        cols: {
          team: "Team",
          gm: "GM",
          gamesPlayed: "Games",
          toMin: "To minimum",
          toMax: "Still possible"
        }
      },

      // Former Champions page
      formerChampions: {
        title: "Former Champions Hall of Fame",
        description: "Honoring the legends who have conquered our league throughout the years. These champions have earned their place in history through skill, dedication, and championship glory.",
        activeGM: "Active GM",
        statsTitle: "Championship Statistics",
        totalChampions: "Total Champions",
        activeChampions: "Still Active",
        totalTitles: "Total Titles",
        mostTitles: "Most Titles",
        legendary: "Legendary Champions (5+ Titles)",
        elite: "Elite Champions (2-4 Titles)",
        oneTime: "Champions (1 Title)",
        legendTitle: "Legend",
        activeIndicator: "Active GM - Click to view profile",
        legendaryIcon: "Legendary Champions (5+ titles)",
        eliteIcon: "Elite Champions (2-4 titles)",
        championIcon: "Champions (1 title)"
      },
      
      // Common terms
      common: {
        loading: "Loading...",
        error: "Error",
        noData: "No data available",
        viewMore: "View More",
        back: "Back",
        save: "Save",
        saving: "Saving...",
        cancel: "Cancel",
        edit: "Edit",
        delete: "Delete",
        add: "Add",
        search: "Search",
        filter: "Filter",
        season: "Season",
        game: "Game",
        games: "Games",
        team: "Team",
        teams: "Teams",
        player: "Player",
        players: "Players",
        stats: "Stats",
        standings: "Standings",
        playoffs: "Playoffs",
        finals: "Finals",
        champions: "Champions",
        wins: "Wins",
        losses: "Losses",
        winPercentage: "Win %",
        points: "Points",
        rebounds: "Rebounds",
        assists: "Assists",
        steals: "Steals",
        blocks: "Blocks",
        conference: "Conference",
        eastern: "Eastern",
        western: "Western",
        position: "Position",
        overall: "Overall",
        badges: "Badges"
      },

      // Home page
      home: {
        title: "Welcome to 2K Bundesliga",
        subtitle: "Cross-Platform Basketball League",
        description: "Xbox Series S/X & PlayStation 5 | NBA 2K26",  
        aboutLeague: "About Our League",
        aboutDescription1: "Welcome to the 2K Bundesliga - the premier cross-platform NBA 2K competitive league! We bring together the best players from Xbox Series S/X and PlayStation 5 to compete using all 30 NBA teams from NBA 2K26.",
        aboutDescription2: "Our league features two conferences with 15 teams each, playing a full season where every team faces each opponent twice. The top 6 teams from each conference qualify for the playoffs, with play-in tournaments determining the final seeds. Experience authentic NBA action with real rosters from 2kratings.com!",
        recentGames: "Recent Games",
        recentGamesFrom: "Recent Games from {{seasonName}}",
        topPerformers: "League Leaders",
        leagueLeaders: "League Leaders",
        currentStandings: "Current Rankings",
        viewFullStandings: "View Full Standings →",
        viewAllGames: "View All Games →",
        viewAllStats: "View All Stats →",
        liveStreams: "Live Streams",
        noActiveStreams: "No streams currently live",
        streamPlaceholder: "Check back later for live NBA 2K streams from our community!",
        eastStandings: "Eastern Conference",
        westStandings: "Western Conference",
        clickForBoxScore: "Click for box score",
        pointsPerGame: "Points Per Game",
        reboundsPerGame: "Rebounds Per Game", 
        assistsPerGame: "Assists Per Game",
        gmGreeting: "Hello {{username}}, {{teamName}} Dashboard",
        lastGameResult: "Last Game",
        noGamesYet: "No games played yet this season",
        seasonOverview: "Season Overview",
        daysToDeadline: "{{days}} days left",
        deadlineEnds: "Ends {{date}}",
        deadlinePassed: "Passed",
        gamesLeftDeadline: "Games left for deadline",
        gamesLeftSeason: "Games left this season",
        gamesToMinDeadline: "Games needed for deadline minimum",
        gamesToMinPlayoffs: "Games needed for playoff qualification",
        gamesPlayed: "Games played",
        unlimited: "Unlimited",
        noLimit: "No limit set",
        qualified: "Qualified!",
        deadlineMet: "Deadline met!",
        currentDeadline: "Current Deadline",
        nextDeadline: "Next Deadline",
        noDeadlines: "No deadlines set",
        vsTeam: "vs",
        win: "W",
        loss: "L",
        viewBoxScore: "View Box Score",
        seasonPassProgress: "Season Pass",
        rewardsProgress: "Rewards & Upgrades",
        currentLevelLabel: "Current Level",
        nextLevelLabel: "Next Level",
        gamesToNextLevel: "{{games}} more games to reach",
        rewardLabel: "Reward",
        noSeasonPass: "No Season Pass configured",
        tierRewardsHint: "Rewards shown for your team's tier",
        allLevelsCompleted: "All levels completed!",
        levelNotReached: "Play games to start your Season Pass",
        // Rookies & Sophs Dashboard
        rookieSophProgress: "Rookies & Sophs",
        noRookieSoph: "No Rookies & Sophs upgrades configured",
        rookieSophAllCompleted: "All upgrade levels completed!",
        rookieSophNotReached: "Play games to earn Rookies & Sophs upgrades",
        upgradeLabel: "Upgrade",
        limitationLabel: "Limitation",
        // Attribute Upgrades Dashboard
        attributeUpgradesProgress: "Attribute Upgrades",
        noAttributeUpgrades: "No attribute upgrades configured",
        attrTotalPoints: "Total Attribute Points Earned",
        attrUpgradesEarned: "Upgrades Earned",
        attrNextUpgrade: "Next upgrade in {{games}} games",
        attrRule: "Every {{games}} games → {{points}} attribute points"
      },

      // All Games page
      allGames: {
        title: "All Games",
        searchPlaceholder: "Search by team name or date...",
        noResults: "No games found matching your search.",
        noGames: "No games played yet this season."
      },

      // Footer
      footer: {
        description: "Cross-Platform Basketball League",
        platforms: "Xbox Series S/X & PlayStation 5 | NBA 2K26",
        rights: "All rights reserved"
      },

      // About page
      about: {
        title: "About 2K Bundesliga",
        mission: "Our Mission",
        missionText1: "The 2K Bundesliga is a competitive cross-platform basketball league that brings together passionate NBA 2K players from Xbox Series S/X and PlayStation 5 platforms. Our mission is to create an authentic, competitive, and inclusive environment where players can showcase their skills using all 30 NBA teams.",
        missionText2: "We strive to replicate the excitement and structure of the real NBA, complete with regular seasons, playoffs, and championship glory. Every game matters, every stat counts, and every champion earns their place in history.",
        leagueStructure: "League Structure",
        easternConference: "Eastern Conference", 
        easternDesc: "15 teams compete in the Eastern Conference, battling for playoff positions and the chance to represent the East in the NBA 2K League Finals.",
        westernConference: "Western Conference",
        westernDesc: "15 teams from the Western Conference fight for supremacy, with the best teams advancing to determine the league champion.",
        whatMakesUsDifferent: "What Makes Us Different",
        crossPlatformPlay: "Cross-Platform Play",
        crossPlatformDesc: "We unite Xbox and PlayStation players in one competitive league, breaking down platform barriers.",
        authenticExperience: "Authentic NBA Experience",
        authenticDesc: "Using real NBA teams and rosters from NBA 2K26, we provide an authentic professional basketball experience.",
        statsTracking: "Comprehensive Stats Tracking",
        statsDesc: "Every point, rebound, assist, and stat is recorded, giving players the recognition they deserve.",
        multipleSeasons: "Multiple Seasons",
        multipleSeasonsDesc: "We typically play 2-3 seasons per NBA 2K version, giving everyone multiple chances to compete for the title.",
        joinOurLeague: "Join Our League",
        joinDescription: "Interested in competing? We welcome skilled players who are committed to fair play, respect, and competitive basketball. Contact us to learn about team ownership opportunities for upcoming seasons.",
        nbaTeamsCount: "NBA Teams",
        gamesPerSeason: "Games per Season", 
        seasonsPerYear: "Seasons per Year",
        format: "League Format",
        teams: "30 NBA teams divided into 2 conferences (Eastern & Western)",
        regularSeason: "Regular Season: Each team plays every opponent twice (58 games total)",
        rosters: "Official NBA 2K26 rosters from www.2kratings.com", 
        platform: "Cross-platform play on Xbox Series S/X and PlayStation 5"
      },

      // Rules page
      rules: {
        title: "League Rules",
        
        // I. Preamble
        preamble: "Preamble",
        preambleText: "Play fair and try to build a game as realistically as possible like the NBA. This includes: Structure in offense. Don't try to play fast break after scoring, install set plays in your offense and don't try to finish every possession with a dunk, but play a variable, considered 'simulation-like' game. Every user certainly has their own playing style, please respect that. Remember, we are a community where every participant has an opinion and is allowed to express it. Be friendly to each other and ensure a smooth running of our league.",

        // II. Fairness and Behavior/Gameplay
        fairnessBehavior: "Fairness and Behavior/Gameplay",
        
        fastBreaks: "Fast Breaks",
        fastBreaksText: "Most important up front: we don't want run & gun shooting! 🙂 7 seconds or less as a continuous game philosophy should please banish from your head. 🙂 You may run a fastbreak at any time after: Steals or Blocks. Of course you may also always finish quickly when the opponent goes for offensive rebounds or doesn't run back. But please refrain from running a fastbreak directly after an inbound. Of course it's different if you're in the final minutes and want to make up a realistic deficit.",
        
        noQuits: "No Quits!",
        noQuitsText: "It is never permitted to leave a game early. If you're hopelessly behind, try to play slowly to limit the damage instead of going even further under. Run your systems and try to practice for the next game and learn the routes. Throwing from half court and putting the controller away will not be tolerated. Play properly to the end, otherwise it could quickly be your last game.",
        
        blowoutGames: "Games with Clear Lead",
        blowoutGamesText: "When the game is decided, give your opponent the chance to make the result somewhat more pleasant to not take away their enjoyment of the game. Of course, they must then also accept the invitation and not just throw every ball forward out of frustration. Therefore: • Substitute starters, give young bench players the chance to prove themselves • No unnecessary reaching fouls • No run and gun • Play out the last possession and let the clock run down • Try out other defensive forms",
        
        disconnects: "Disconnects",
        disconnectsText: "In case of a disconnect by the trailing team with more than 20 points behind in the 3rd or more than 10 in the 4th quarter, as well as - regardless of the deficit - in the last two minutes of the 4th quarter or overtime, the game will be counted for the leading team. In case of a disconnect by the leading team, the game will be repeated. Special cases are at the discretion of the admins!",
        
        inbounds: "Inbounds",
        inboundsText: "Inbound passes may generally be intercepted, except after baskets from the baseline and not by the direct defender of the inbounder. In the last two minutes (4th quarter) this restriction is lifted. If it does happen that an inbound pass is intercepted by the direct defender, give the ball back to the opponent.",
        
        intentionalFouls: "Intentional Fouls",
        intentionalFoulsText: "Before the last two minutes of a game, a maximum of 3 intentional fouls (with the Select button) are allowed; after that there is no limit. They are there to stop the fastbreak or end a mismatch. Shortly before the end, care should be taken to get fouled. Just like with +3 don't foul extra.",
        
        pausingStats: "Pausing & Statistics",
        pausingStatsText: "Each player may press pause 5 times per game to check statistics (e.g. number of shots). If someone needs to do something and needs about 5 minutes of game stoppage, please announce it beforehand via voice chat. The opponent has to accept this within reason. Please refrain from pressing start during very frustrating actions.",

        // III. Requirements
        requirements: "Requirements",
        
        numberOfGames: "Number of Games",
        numberOfGamesText: "In each season there are fixed game upper and lower limits, so that the difference between teams is kept as small as possible. If you have a reason why you can't reach the games (vacation, school/uni, work etc.), then please let us know. In individual cases there is the possibility that a user may 'play ahead' - if he goes on vacation for 3 weeks for example. Such things must be discussed with the admin team beforehand and announced accordingly.",
        
        shotLimitation: "Shot Limitation",
        shotLimitationText: "You should always keep in mind that basketball is a team sport and therefore the ball should gladly be shared. Of course you should also use the strengths of your players/your team but nobody wants to have statistics with 40 shots one player, the second ten and the third five and the rest zero. Please make sure that you are co-responsible for how pleasant a 2K round with you will be! The league admin will intervene in case of conspicuous (negative) gameplay and possibly ban players/sanction GMs.",
        
        lockOnDefense: "Lock-On Defense",
        lockOnDefenseText: "On-ball defense is mandatory. We want to play player against player here and not player against AI. You can try to adjust your defense by briefly selecting another player (e.g. against a certain system or mismatch). Also use the possibilities of zone defense, whereby here too it must be ensured that the ball-defending player is selected by the next defender. The adjusting should not be exploited and really only be tried in necessary situations. Defense away from the ball handler is forbidden.",
        
        pointsInZone: "Points in the Zone",
        pointsInZoneText: "Each team should try to make a maximum of 55% of all points within the zone (guideline). Lower values are expressly desired. No fastbreak points are deducted and in case of particularly serious violations, the admin team reserves the right to annul a game or even count it against the rule-violating team. Exceeding is generally not a reason for exclusion, but try to move within the framework.",
        
        headsetRequirement: "Headset Requirement",
        headsetRequirementText: "Headset requirement applies to all games. This is to ensure that disagreements and other problems can be clarified quickly and easily and therefore fewer discussions take place before and after games. If you can't talk in the game (wife or children are sleeping), still put on the headset and use it in cases where there are unclear situations. If the headset is defective, get a replacement as quickly as possible. In that case, only then play games again.",
        
        media: "Media",
        mediaText: "The community is happy about every team report. It would be desirable if every GM during the season either keeps a small blog or posts reports from time to time. During the playoffs, the winner writes a report/uploads the stats for each game. Please also record highlights and post them in the corresponding highlight thread!",

        // IV. Standings, Play-In & Playoffs Placement and Lottery
        standingsPlayoffs: "Standings, Play-In & Playoffs Placement and Lottery",
        
        endOfSeason: "4.1 At the end of the regular season, a table placement is created. Depending on the placement, the probabilities for the next draft increase.",
        
        playoffQualification: "4.2 Playoff Qualification",
        playoffQualificationText: "Who has the minimum number of games qualifies for the playoffs - provided the places are free. The placement is composed of the following categories: Number of wins, Number of games, Direct comparison",
        
        playInTournament: "4.3 Play-In Tournament",
        playInTournamentText: "Play-In Tournament can always take place, provided we have ten teams in the East as well as in the West that have reached the game number. There will also be exceptions where we will remove the Play-In Tournament by voting before a season.",
        
        lottery: "4.4 Lottery",
        lotteryText: "Since Season 33 we have a lottery that is based on the real NBA. With reversed record, the first 14 teams land in the lottery. Should picks 1-4 not land among the first four teams, then they are automatically classified from place 5. Teams that do not land in the lottery are automatically classified from pick 15/after the lottery. In the 1st draft of a 2K part, all participants have the same probability. From the 2nd draft of a 2K part, the placements and probabilities come into play.",

        // V. Trades & Roster Management
        tradesRosterManagement: "Trades & Roster Management",
        tradesIntro: "These are the general trade rules as they usually apply for a season. For the respective current season, (including short-term) deviations may apply. We play without cap!",
        
        roster: "5.1 Roster",
        rosterText: "We start with the team rosters from the draft (draft day applies). Each team determines a Can't Cut player and makes this known with the publication of the trade threads. This must be one of the top 3 OVR. These players may not be traded and must be determined before the 1st trade. Exceptions are fantasy seasons.",
        
        trades: "5.2 Trades",
        tradesText: "Except for the Can't Cut player, every player can be traded. However, there are some restrictions. In a trade with the same number of players, a maximum +-3 OVR difference may result. Can't Cut players can change during the season.",
        
        unequalTrades: "5.2.1 Unequal Number of Player Trades",
        unequalTradesText: "If you want to trade one player for two/there is an odd number (3:2 etc.), the following rule applies: You add the sum of all players and divide the sum by the number of players. The OVR difference in trades with unequal player numbers may not be more than +-3.",
        
        tradeStaggering: "5.3 Trade Staggering",
        tradeStaggeringText: "The season pass applies in the season. There the trade staggering is also regulated. You can look this up under ⁠nepoznato. For the player number, always the number of shipped players counts - not received.",
        
        tradeValidity: "5.4 Trade Validity",
        tradeValidityText: "A trade is only valid and may be entered in the trade thread if all parties meet the required criteria (game number in stats tool + available player number). Trades for the future may not be entered."
      },

      // Team Owners page
      teamOwners: {
        title: "Team Owners Directory",
        subtitle: "Meet the owners behind each team in our league",
        loadingOwners: "Loading team owners...",
        totalTeamOwners: "Total Team Owners",
        streamersOnTwitch: "Streamers on Twitch", 
        totalGamesPlayed: "Total Games Played",
        team: "TEAM",
        gamertag: "GAMERTAG",
        watchOnTwitch: "WATCH ON TWITCH",
        record: "RECORD",
        winPercentage: "WIN %",
        joined: "Joined:",
        teamAssigned: "Team Assigned:",
        noTeamOwnersFound: "No team owners found"
      },

      // Apply page
      apply: {
        title: "Apply to Join 2K Bundesliga",
        subtitle: "Fill out the application below and we'll review it ASAP",
        becomeTeamOwner: "Become a Team Owner",
        controlNBATeam: "Control an NBA team",
        controlTeam: "Control an NBA team",
        xboxPlaystation: "Xbox & PlayStation",
        xboxPlayStation: "Xbox & PlayStation",
        crossPlatformLeague: "Cross-platform league",
        crossPlatform: "Cross-platform league",
        competeForGlory: "Compete for Glory",
        competeGlory: "Compete for Glory",
        championshipTrophy: "Championship trophy awaits",
        championship: "Championship trophy awaits",
        personalInformation: "Personal Information",
        personalInfo: "Personal Information",
        firstName: "First Name",
        firstNamePlaceholder: "Enter your first name",
        lastName: "Last Name",
        lastNamePlaceholder: "Enter your last name",
        gamingInformation: "Gaming Information",
        gamingInfo: "Gaming Information",
        nbaGamertag: "NBA 2K Gamertag",
        gamertag: "NBA 2K Gamertag",
        gamertagPlaceholder: "Enter your gamertag",
        gamingPlatform: "Gaming Platform",
        platform: "Gaming Platform",
        selectPlatform: "Select your platform",
        xboxSeriesLabel: "Xbox Series S/X",
        xboxSeries: "Xbox Series S/X",
        playstationLabel: "PlayStation 5",
        playStation5: "PlayStation 5",
        emailAddress: "Email Address",
        email: "Email Address",
        emailPlaceholder: "john@example.com",
        inviteCodeEmail: "We'll send your invite code to this email",
        emailNote: "We'll send your invite code to this email",
        teamPreference: "Team Preference",
        preferredTeam: "Preferred Team (Optional)",
        noPreference: "No preference (Admin will assign)",
        onlyAvailableTeams: "Select your preferred team or leave blank for admin assignment. Only available teams are shown.",
        teamNote: "Select your preferred team or leave blank for admin assignment. Only available teams are shown.",
        additionalInformation: "Additional Information",
        additionalInfo: "Additional Information",
        nbaExperience: "NBA 2K Experience",
        experience: "NBA 2K Experience",
        experiencePlaceholder: "Tell us about your NBA 2K experience, skill level, years playing, etc.",
        whyJoin: "Why do you want to join?",
        reason: "Why do you want to join?",
        reasonPlaceholder: "What motivates you to join the 2K Bundesliga?",
        weeklyAvailability: "Weekly Availability",
        availability: "Weekly Availability",
        availabilityPlaceholder: "e.g., Weekday evenings, Weekend mornings, etc.",
        submitApplication: "Submit Application",
        submittingApplication: "Submitting Application...",
        submitError: "An error occurred. Please try again.",
        alreadyHaveCode: "Already have an invite code?",
        registerHere: "Register here",
        applicationSubmitted: "Application Submitted!",
        thankYou: "Thank you for applying to join the 2K Bundesliga! An admin will review your application and send you an invite code via email soon.",
        whatHappensNext: "What happens next?",
        applicationReceived: "Your application has been received",
        adminReview: "An admin will review your application (usually within 24-48 hours)",
        receiveEmail: "You'll receive an invite code via email",
        completeRegistration: "Use the code to complete your registration",
        useCodeToRegister: "Use the code to complete your registration",
        backToHome: "Back to Home",
        browseTeams: "Browse Teams"
      },

      // Admin area
      admin: {
        // Layout and navigation
        panel: "Admin Panel",
        dashboard: "Dashboard",
        gamesManagement: "Games Management",
        playoffManagement: "Playoff Management",
        playerManagement: "Player Management",
        rosterManagement: "Roster",
        tradeManagement: "Trade Management",
        teamManagement: "Team Management",
        inviteCodes: "Invite Codes",
        userManagement: "User Management",
        documentation: "Documentation",
        settings: "Settings",
        teamSettings: "Team Settings",
        changePassword: "Change Password",
        logout: "Logout",
        backToWebsite: "Back to Website",
        
        // Documentation translations
        adminDocumentation: "Admin Documentation",
        gmDocumentation: "GM Documentation",
        
        // Admin Documentation sections
        adminDocs: {
          // Roster: inactive players (the same flag GMs set themselves)
          rosterAdminNote: "GMs set this themselves under \u201cRoster\u201d. As an admin you can pick any team there and switch for them \u2014 useful when a roster still holds far more than 15 players at the start of a season.",
          title: "Admin Documentation",
          subtitle: "Complete guide to managing the 2K Bundesliga",
          overview: "Overview",
          seasons: "Season Management", 
          fantasy: "Fantasy Seasons",
          fantasyDraft: "Fantasy Draft",
          players: "Player Management",
          games: "Game Scheduling",
          ocr: "OCR Screenshots",
          playoffs: "Playoffs Management",
          trades: "Trade Management",
          invites: "Invite System",
          users: "User Management",
          backup: "Data Backup",
          troubleshooting: "Troubleshooting",
          
          // Section content
          overviewTitle: "2K Bundesliga Admin Panel Overview",
          mainFunctions: "Main Functions",
          typicalFlow: "Typical Season Flow",
          importantNotes: "Important Notes",
          fantasyDraftTitle: "Fantasy Draft Management",
          fantasyDraftDesc: "Fantasy Draft allows you to create snake-style drafts where GMs take turns picking players to build their teams.",
          ocrTitle: "OCR Screenshot Upload",
          ocrDesc: "Optical Character Recognition (OCR) allows automatic extraction of player statistics from NBA 2K screenshots.",
          backupTitle: "Data Backup & Recovery",
          backupDesc: "Regular backups are crucial for protecting your league data."
        },

        // Can't Cut Player translations
        cantCutPlayer: "Can't Cut Player",
        cantCutRules: "Can't Cut Player Rules",
        cantCutRule1: "You can designate one player from your top 3 overall ratings as your Can't Cut player",
        cantCutRule2: "This player cannot be traded and will block any trades that include them",
        cantCutRule3: "Once set, your Can't Cut player selection is permanent and cannot be changed",
        cantCutRule4: "You must designate a Can't Cut player before you can propose trades",
        currentCantCutPlayer: "Current Can't Cut Player",
        selectCantCutPlayer: "Select Can't Cut Player",
        selectNewCantCutPlayer: "Select New Can't Cut Player",
        cantCut: "Can't Cut",
        overall: "Overall",
        position: "Position",
        cantCutPlayerUpdated: "Can't Cut player updated successfully",
        errorUpdatingCantCut: "Error updating Can't Cut player",
        errorFetchingSeason: "Error fetching current season",
        errorLoadingPlayers: "Error loading players",
        noEligiblePlayers: "No eligible players available",
        saving: "Saving...",
        setCantCutPlayer: "Set Can't Cut Player",
        selected: "Selected",
        currentCantCut: "Current Can't Cut",
        clearSelection: "Clear Selection",
        pleaseSelectPlayer: "Please select a player first",
        cantCutAlreadySet: "A Can't Cut player has already been set and cannot be changed",
        cantCutConfirmation: "Confirm Can't Cut Player Selection",
        cantCutWarning: "WARNING: Once you set a Can't Cut player, this selection is permanent and cannot be changed. Are you sure you want to designate this player as your Can't Cut player?",
        confirmSelection: "Confirm Selection",
        permanentSelection: "This selection is permanent",
        otherSettings: "Other Settings",
        moreSettingsComingSoon: "More settings options coming soon",
        
        // Player Management
        playerManagementTitle: "Player Management",
        allTeams: "All Teams",
        searchPlayers: "Search players...",
        
        // Team Management
        teamName: "Team Name",
        
        // GM Documentation sections
        gmDocs: {
          title: "GM Documentation",
          subtitle: "Complete guide for team owners and general managers",
          overview: "Overview",
          profile: "Profile Management",
          team: "Team Management", 
          trades: "Trade Proposals",
          games: "Game Results",
          schedule: "My Schedule",
          playoffs: "Playoff Games",
          settings: "Settings",
          
          // Section content
          overviewTitle: "Team Owner Guide",
          profileTitle: "Profile Management",
          profileDesc: "Manage your personal profile, gamertag, and streaming information.",
          teamTitle: "Team Management", 
          teamDesc: "View your roster, player stats, and team information.",
          tradesTitle: "Trade Proposals",
          tradesDesc: "Propose trades with other teams and manage trade offers.",
          gamesTitle: "Game Result Entry",
          gamesDesc: "Enter scores and statistics for your team's games.",

          // Roster management: GMs flag players they do not use as inactive
          rosterTitle: "Roster: active and inactive players",
          rosterIntro: "Your 2K roster usually holds more than 15 players, but only some of them actually play. Mark the rest as inactive — they stay on your roster and keep their history, they just disappear from everything the league looks at.",
          rosterWhere: "Where to do it",
          rosterWhereSteps: {
            open: "Open \u201cRoster\u201d in the left admin menu",
            toggle: "Click \u201c\u2715 Deactivate\u201d next to the player",
            undo: "Inactive players move to the bottom block \u2014 \u201c\u2713 Activate\u201d brings them back at any time"
          },
          rosterEffect: "What inactive means",
          rosterEffectList: {
            hidden: "Gone from your public team page, the player list and the player comparison",
            leaders: "Gone from the stat leaderboards and the award stat leaders \u2014 even with stats already recorded",
            entry: "Gone from stat entry; the \u201cShow inactive players\u201d switch brings them back if someone did play",
            kept: "Box scores, team totals and the player page stay untouched \u2014 nothing is deleted",
            visible: "Visible only in the admin area: this page and the player list"
          },
          rosterNotes: "Good to know",
          rosterNotesList: {
            noLimit: "No hard limit \u2014 the counter just turns orange above 15 active players",
            anytime: "You can switch at any time, also mid-season",
            reset: "After a trade, a release or a new assignment the player arrives active again",
            perSeason: "The status belongs to the season \u2014 past seasons keep their own"
          }
        },
        
        // Dashboard titles and descriptions
        superAdminDashboard: "Super Admin Dashboard",
        teamOwnerDashboard: "Team Owner Dashboard",
        welcomeBack: "Welcome back, {{username}}!",
        adminWelcomeText: "Manage your NBA 2K Bundesliga from this admin panel.",
        ownerWelcomeText: "You are managing {{teamName}}.",
        managingTeam: "Managing: {{teamName}}",
        
        // Stats cards
        totalTeams: "Total Teams",
        totalPlayers: "Total Players", 
        activeSeason: "Active Season",
        totalUsers: "Total Users",
        
        // Quick Actions widgets
        quickActions: "Quick Actions",
        createNewSeasons: "Create new seasons, manage current season settings",
        scheduleGames: "Schedule games, enter results, view all matches",
        managePlayerRosters: "Manage player rosters, ratings, and statistics",
        reviewTrades: "Review and approve/reject trade proposals", 
        manageTeamsAssign: "Manage teams, assign owners, update team info",
        manageUserAccounts: "Manage user accounts, roles, and permissions",
        reviewApplications: "Review applications, send invite codes to new owners",
        viewScheduleResults: "View your schedule, enter game results",
        proposeTradesHistory: "Propose trades, view trade history",
        updateTeamInfo: "Update team information and preferences",
        
        // Owner-specific labels
        myGames: "My Games",
        myTrades: "My Trades", 
        teamSettings: "Team Settings",
        
        // Recent Activity
        recentActivity: "Recent Activity",
        noRecentActivity: "No recent activity to display.",
        
        // Common admin actions
        create: "Create",
        update: "Update",
        assign: "Assign",
        unassign: "Unassign",
        approve: "Approve",
        reject: "Reject",
        activate: "Activate",
        deactivate: "Deactivate",
        reset: "Reset",
        generate: "Generate",
        import: "Import",
        export: "Export",
        
        // Season Management
        seasonManagement: {
          title: "Season Management",
          currentSeason: "Current Season",
          seasonName: "Season Name", 
          seasonType: "Season Type",
          regularSeason: "Regular Season",
          customSeason: "Custom Season",
          fantasyDraft: "Fantasy Draft",
          startDate: "Start Date",
          endDate: "End Date",
          isActive: "Active Season",
          createSeason: "Create New Season",
          updateSeason: "Update Season",
          activateSeason: "Activate Season", 
          deactivateSeason: "Deactivate Season",
          seasonCreated: "Season created successfully",
          seasonUpdated: "Season updated successfully",
          seasonActivated: "Season activated successfully",
          seasonDeactivated: "Season deactivated successfully",

          // Season setup assistant (AP6)
          setupTab: "Setup",
          setupTitle: "Season setup checklist",
          setupDescription: "Live status of each setup step. Each button can be run repeatedly without harm.",
          setupRefresh: "Refresh",
          setupLoading: "Loading status…",
          setupLoadError: "Failed to load setup status",
          setupDone: "Done",
          setupOpen: "Open",
          setupMaterializing: "Materializing…",
          setupMaterializeDone: "Roster materialized: {{created}} created, {{skipped}} skipped ({{total}} in standard pool).",
          setupMaterializeError: "Failed to materialize roster",
          setupStepRoster: "Roster materialized",
          setupStepRosterStatus: "{{count}} roster entries for this season",
          setupActionMaterialize: "Materialize roster",
          setupStepGames: "Schedule generated",
          setupStepGamesStatus: "{{total}} games, {{played}} played",
          setupActionGoGames: "Open Games",
          setupStepDeadlines: "Deadlines",
          setupStepSeasonPass: "Season Pass",
          setupStepRookieSoph: "Rookies & Sophs",
          setupStepCountStatus: "{{count}} configured",
          setupActionGoTab: "Go to tab",
          setupStepFreeAgency: "Free Agency",
          setupStepFreeAgencyYes: "Configured for this season",
          setupStepFreeAgencyNo: "Not set up yet",
          setupActionGoFreeAgency: "Open Free Agency",
          setupStepGms: "GMs with a team",
          setupStepGmsStatus: "{{count}} GMs assigned to a team",
          setupActionGoUsers: "Open Users",
          setupStepGmHistory: "GM history saved",
          setupStepGmHistoryStatus: "{{count}} GM→team records frozen for this season",
          setupActionSnapshot: "Save GM history",
          setupSnapshotting: "Saving…",
          setupSnapshotDone: "GM history saved: {{created}} GM→team records frozen ({{total}} GMs). Run this before reassigning teams.",
          setupSnapshotAlready: "GM history already saved for this season ({{existing}} records); left unchanged.",
          setupSnapshotError: "Failed to save GM history",

          // Gameplay Settings tab
          generalSettingsTab: "General Settings",
          gameplaySettingsTab: "Gameplay Settings",
          seasonSettings: "Season Settings",
          seasonSettingsDescription: "Define baseline rules for game count and playoff qualification.",
          maxGames: "Maximum Number of Games",
          maxGamesHelp: "Maximum number of games per team this season. Leave empty for no limit.",
          minGamesForPlayoffs: "Minimum Games for Playoffs",
          minGamesForPlayoffsHelp: "Minimum games a team must play to qualify for playoffs.",
          maxTradesPerTeam: "Maximum Trades per Team",
          maxTradesPerTeamHelp: "Trades a team may make per season. Pending offers already reserve a slot. Leave empty for no limit.",
          maxTradedPlayersPerTeam: "Maximum Traded Players per Team",
          maxTradedPlayersPerTeamHelp: "Players a team may give away per season, summed over all its trades. Leave empty for no limit.",
          saveGameplaySettings: "Save Season Settings",
          gameplaySettingsSaved: "Season settings saved successfully",
          noLimit: "No limit",

          // Deadlines
          deadlines: "Deadlines",
          deadlinesDescription: "Define milestones with game targets that teams must meet by a given date.",
          addDeadline: "Add Deadline",
          editDeadline: "Edit Deadline",
          saveDeadline: "Save Deadline",
          confirmDeleteDeadline: "Are you sure you want to delete this deadline?",
          noDeadlines: "No deadlines defined yet. Click \"Add Deadline\" to create one.",
          deadlineLabel: "Label / Name",
          deadlineLabelPlaceholder: "e.g., Matchday 1 deadline",
          deadlineStartDate: "Start Date",
          deadlineEndDate: "End Date",
          deadlineMinGames: "Min Games",
          deadlineMaxGames: "Max Games (optional)",
          deadlineMaxGamesHelp: "Leave empty for no upper limit.",

          // Season Pass
          seasonPassTab: "Season Pass",
          seasonPass: "Season Pass",
          seasonPassDescription: "Define progression levels based on games played. Each level grants a reward when reached.",
          tierColumn: "Tier texts",
          tierEmpty: "{{tier}}: no separate text — falls back to the reward description",
          tierRewards: "Tier-specific rewards",
          tierRewardsHint: "Optional. Each GM only sees the text for their team's tier. Empty fields fall back to the reward description.",
          addLevel: "Add Level",
          editLevel: "Edit Level",
          saveLevel: "Save Level",
          confirmDeleteLevel: "Are you sure you want to delete this level? Remaining levels will be renumbered.",
          noLevels: "No season pass levels defined yet. Click \"Add Level\" to create one.",
          levelNumber: "Level",
          gamesRequired: "Games Required",
          rewardDescription: "Reward / headline",
          rewardDescriptionPlaceholder: "e.g., Training Camp, Second Chance",
          rewardDescriptionHint: "Shown to every GM whose tier has no separate text below.",
          icon: "Icon (optional)",
          selectIcon: "Select Icon",
          noIcon: "No Icon",
          levelSaved: "Level saved successfully",
          levelDeleted: "Level deleted and levels renumbered",
          maxGamesNote: "Season max: {{max}} games",
          noMaxGamesNote: "No season game limit set",

          // Rookies & Sophs
          rookieSophTab: "Rookies & Sophs",
          rookieSoph: "Rookies & Sophs Upgrades",
          rookieSophDescription: "Define upgrade levels for rookies and sophomores based on games played. Each level can include a limitation.",
          noRookieSophLevels: "No Rookies & Sophs levels defined yet. Click \"Add Level\" to create one.",
          upgradeDescription: "Upgrade Description",
          upgradeDescriptionPlaceholder: "e.g., +2 Badge Points, Silver to Gold upgrade",
          limitationDescription: "Limitation",
          limitationDescriptionPlaceholder: "e.g., Only applicable to badges below Gold",

          // Attribute Upgrades
          attributeUpgradesTab: "Attribute Upgrades",
          attributeUpgrades: "Attribute Upgrades",
          attributeUpgradesDescription: "Configure how attribute points are earned based on games played. Points are granted at regular intervals.",
          attrGamesInterval: "Games Interval",
          attrGamesPlaceholder: "e.g., 6",
          attrGamesHelp: "Number of games between each attribute upgrade.",
          attrPoints: "Attribute Points",
          attrPointsPlaceholder: "e.g., 4",
          attrPointsHelp: "Number of attribute points granted per interval.",
          attrPreview: "Preview",
          attrPreviewText: "Every {{games}} games → {{points}} attribute points",
          attributeUpgradesSaved: "Attribute upgrade settings saved!",

          // Validation messages
          validation: {
            tradeLimitInvalid: "Trade limit must be a valid non-negative number",
            labelRequired: "Label is required.",
            startDateRequired: "Start date is required.",
            endDateRequired: "End date is required.",
            startBeforeEnd: "Start date must not be after end date.",
            minGamesRequired: "Minimum number of games is required.",
            minGamesInvalid: "Minimum games must be a valid non-negative number.",
            maxGamesInvalid: "Maximum games must be a valid non-negative number.",
            maxGamesGteMin: "Maximum games must be greater than or equal to minimum games.",
            maxLtMinPlayoffs: "Season maximum games must not be lower than minimum games for playoffs.",
            attrGamesInvalid: "Games interval must be a positive number.",
            attrPointsInvalid: "Attribute points must be a positive number."
          }
        }
      },

      // Analytics / Data & Statistics
      analytics: {
        title: "Data & Statistics",
        subtitle: "Deep dive into league performance data",
        currentSeason: "Current Season",
        active: "Active",
        noData: "No data available",
        backToAnalytics: "Back to Analytics",
        backToRankings: "Back to Rankings",
        cards: {
          teamRankings: { title: "Team Rankings", desc: "Compare teams by wins, scoring, and more" },
          gmRankings: { title: "GM Rankings", desc: "See how GMs stack up across the league" },
          headToHead: { title: "Head to Head", desc: "Compare two teams directly" },
          formTrends: { title: "Form & Trends", desc: "Team momentum and scoring trends" },
          homeAway: { title: "Home / Away Splits", desc: "Performance breakdown by location" }
        },
        teamRankings: {
          title: "Team Rankings",
          subtitle: "Complete team standings with advanced stats"
        },
        gmRankings: {
          title: "GM Rankings",
          subtitle: "General Manager performance leaderboard"
        },
        teamDetail: {
          title: "Team Statistics",
          rank: "Rank"
        },
        gmDetail: {
          title: "GM Statistics",
          rank: "Rank",
          team: "Team"
        },
        headToHead: {
          title: "Head to Head",
          subtitle: "Compare two teams directly",
          team1: "Team 1",
          team2: "Team 2",
          selectTeam: "Select a team...",
          noGames: "These teams haven't played each other yet",
          totalGames: "Total Games",
          totalPoints: "Total Points",
          comparison: "Comparison",
          matchHistory: "Match History"
        },
        compliance: {
          title: "Compliance Analytics",
          subtitle: "Data quality flags and anomaly detection (Admin only)",
          flaggedGames: "Flagged Games",
          showAll: "Show All",
          noFlags: "No flagged games found — everything looks clean!",
          flag: "Flag",
          matchup: "Matchup",
          details: "Details",
          teamAnomalies: "Team Anomalies",
          blowoutPct: "Blowout %",
          flags: {
            missing_stats: "Missing Stats",
            zeroed_stats: "Zeroed Stats",
            blowout: "Blowout",
            high_scoring: "High Scoring"
          }
        },
        cols: {
          rank: "#",
          team: "Team",
          gm: "GM",
          gp: "GP",
          w: "W",
          l: "L",
          pct: "PCT",
          ppg: "PPG",
          oppg: "OPPG",
          diff: "DIFF",
          home: "Home",
          away: "Away",
          streak: "Streak",
          last5: "Last 5",
          last10: "Last 10",
          record: "Record",
          date: "Date",
          location: "Location",
          opponent: "Opponent",
          result: "Result",
          score: "Score",
          scored: "Scored",
          allowed: "Allowed"
        },
        tabs: {
          overview: "Overview",
          trends: "Trends",
          splits: "Splits",
          history: "History"
        },
        charts: {
          winPctOverTime: "Win % Over Time",
          scoringTrend: "Scoring Trend (5-Game Rolling Avg)",
          diffTrend: "Point Differential Trend",
          gameResults: "Game Results",
          gameNumber: "Game #",
          game: "Game"
        },
        splits: {
          home: "Home",
          away: "Away",
          homeShort: "H",
          awayShort: "A"
        }
      },
      bench: {
        benchedSection: "Inactive ({{count}})",
        activeCount: "{{active}} active · {{benched}} inactive",
        inactiveBadge: "Inactive",
        activeBadge: "Active",
        deactivate: "✕ Deactivate",
        activate: "✓ Activate",
        confirmDeactivate: "Mark this player as inactive? He stays on the roster but disappears from the public pages and from stat entry.",
        confirmActivate: "Activate this player again?",
        updateFailed: "Failed to update player status",
        showBenched: "Show inactive players",
        benchedHint: "inactive",
        benchedAdminBadge: "Inactive (GM)"
      },

      // Roster management in the admin area: GMs flag players as inactive
      roster: {
        title: "Roster",
        intro: "Mark players you do not use as inactive. They stay on the roster but disappear from the public team page, the player list and the stat leaderboards, and no longer show up in stat entry. You can bring them back at any time.",
        selectTeam: "Team",
        player: "Player",
        badges: "Badges",
        status: "Status",
        action: "Action",
        empty: "No players on this roster.",
        noTeam: "You are not assigned to a team. Contact an admin.",
        loadFailed: "Failed to load roster",
        overTarget: "more than {{target}} active",
        benchedNote: "only visible here",
        dismiss: "Dismiss"
      },

      // Stat entry: what the screenshot-to-roster name matching could not resolve
      ocrReview: {
        title: "Check this before submitting",
        intro: "The screenshot was read correctly, but these lines could not be assigned to a roster player with certainty. Anything listed here is NOT saved unless you add it by hand below.",
        unmatched: "No player found ({{count}})",
        unmatchedHint: "No roster player matches this name. Add the line by hand below.",
        offRoster: "Not on this roster ({{count}})",
        offRosterHint: "Matched to a player who belongs to another team — usually a trade that has not been entered yet. Discarded, because the line would otherwise be invisible here but still submitted.",
        ambiguous: "Ambiguous ({{count}})",
        ambiguousHint: "Several roster players fit this name. Please check the assignment.",
        lineStats: "{{minutes}} MIN, {{points}} PTS",
        scoreCheckTitle: "Points do not add up",
        scoreMismatch: "{{team}}: the stat lines total {{sum}}, the final score says {{score}} ({{diff}} missing)",
        scoreOk: "Points match the final score.",
        staleBuild: "This page has been open since the last update and is still running an old version. Please reload (Cmd/Ctrl + R) before reading the screenshots — otherwise players can be dropped from the box score without notice.",
        confirmMismatch: "The stat lines do not add up to the final score:\n\n{{details}}\n\nUsually a player from the screenshot is missing. Submit anyway?",
        confirmMismatchLine: "{{team}}: {{sum}} instead of {{score}} ({{diff}} missing)",
        confirmNoStats: "Not a single stat line has been entered.\n\nSubmitting now deletes any stats already stored for this game. If you meant to read the screenshots, cancel and click \"Extract Stats\" first.\n\nSubmit anyway?"
      },
      finals: {
        notStarted: "Finals have not started yet."
      },

      // Player comparison (head-to-head)
      compare: {
        title: "Compare Players",
        subtitle: "Head-to-head — career stats, attributes and badges.",
        playerA: "Player A",
        playerB: "Player B",
        selectPlayer: "Select player",
        searchPlaceholder: "Search player…",
        freeAgent: "Free Agent",
        noResults: "No players found.",
        swap: "Swap",
        clear: "Clear",
        pickBoth: "Pick two players to see the head-to-head comparison.",
        pickOther: "Pick a second player to compare.",
        regularSeason: "Regular Season",
        playoffs: "Playoffs",
        statsTitle: "Career Stats",
        attributesTitle: "Attributes",
        badgesTitle: "Badges",
        noStats: "No stats recorded.",
        noAttributes: "No attribute data yet.",
        noBadges: "No badge data yet.",
        overall: "Overall",
        potential: "Potential",
        totalAttributes: "Total Attributes",
        compareCta: "Compare",
        comparePlayersCta: "Compare Players",
        tiers: {
          legendary: "Legendary",
          hof: "Hall of Fame",
          gold: "Gold",
          silver: "Silver",
          bronze: "Bronze",
          total: "Total"
        },
        categories: {
          outsideScoring: "Outside Scoring",
          insideScoring: "Inside Scoring",
          athleticism: "Athleticism",
          playmaking: "Playmaking",
          defense: "Defense",
          rebounding: "Rebounding",
          intangibles: "Intangibles"
        }
      },

      // Trade budget per season + Discord announcement
      trades: {
        budget: {
          line: "Season budget: {{trades}}/{{maxTrades}} trades · {{players}}/{{maxPlayers}} players given away",
          afterThisTrade: "→ with this trade: {{trades}} trades / {{players}} players",
          rule: "Max {{maxTrades}} trades and {{maxPlayers}} players given away per team and season (pending offers already count)",
          blockedTitle: "Season trade budget exceeded",
          blockedTrades: "{{team}} has already used all {{max}} trades of this season ({{used}} used, pending offers included).",
          blockedPlayers: "{{team}} would give away {{adding}} more player(s) and exceed the limit of {{max}} ({{used}} already used).",
          serverBlocked: "Season trade budget exceeded"
        },
        discord: {
          title: "Discord announcement",
          hint: "German text for the Discord channel — copy it and post it there.",
          generate: "Create text",
          copy: "Copy",
          copied: "Copied!",
          copyFailed: "Could not copy the text. Please select it manually.",
          createdWithText: "Trade offer created — the Discord text is ready below the offer."
        },
        // Three-team trades
        multi: {
          addThirdTeam: "➕ Add third team",
          removeThirdTeam: "Remove third team",
          thirdTeamLabel: "Third team",
          selectTeam: "Select team...",
          threeTeamBadge: "3-team trade",
          hint: "In a three-team trade every team gives players away and receives players. Pick a target team for each player you select.",
          playersFrom: "Players from {{team}} ({{count}}/{{max}})",
          targetTeam: "to",
          noTargetYet: "Pick target",
          givesAway: "Gives away",
          receives: "Receives",
          valid: "Three-team trade is valid",
          invalid: "Three-team trade is not within the rules",
          incomplete: "Every team has to give away at least one player, and every selected player needs a target team.",
          ruleTotal: "Rule 5.2 (total): gives {{give}} OVR, receives {{receive}} OVR, difference {{difference}}",
          ruleAvg: "Rule 5.2.1 (average): gives {{giveTotal}} ÷ {{giveCount}} = {{giveAvg}} OVR, receives {{receiveTotal}} ÷ {{receiveCount}} = {{receiveAvg}} OVR, difference {{difference}}",
          rule: "Three-team trades: every team gives away and receives players, and rule 5.2 / 5.2.1 is checked per team",
          approvalTitle: "Approvals",
          approved: "approved",
          awaiting: "still open",
          alreadyApproved: "Already approved",
          needsBothPartners: "The players move once both partner teams have approved.",
          approvalRecorded: "Your approval is saved — waiting for the other partner team.",
          confirmAccept: "Accept this three-team trade? The players move as soon as both partner teams have approved."
        }
      }
    }
  },
  de: {
    translation: {
      // Navigation (German - informal "du")
      nav: {
        home: "Startseite",
        standings: "Tabelle",
        gamePeriods: "Spielperioden",
        stats: "Statistiken",
        playoffs: "Playoffs",
        finals: "Finale",
        champions: "Champions",
        formerChampions: "Ehemalige Champions",
        teams: "Teams",
        players: "Spieler",
        rules: "Regeln",
        about: "Über uns",
        teamOwners: "Team-Besitzer",
        apply: "Jetzt bewerben",
        login: "Anmelden",
        register: "Registrieren",
        settings: "Einstellungen",
        logout: "Abmelden",
        admin: "Admin",
        league: "Liga",
        news: "News",
        info: "Info",
        ownerNews: "Team-News",
        tradeNews: "Trade-News",
        analytics: "Statistiken"
      },

      // Spielperioden-Seite
      gamePeriods: {
        title: "Spielperioden",
        subtitle: "Absolvierte Spiele je Team in {{season}} — nur Teams mit GM.",
        running: "laufend",
        upcoming: "anstehend",
        finished: "abgeschlossen",
        daysLeft: "noch {{days}} Tage",
        minGames: "Mindestanzahl Spiele",
        maxGames: "Maximale Anzahl Spiele",
        cumulativeHint: "Kumulativ gezählt: alle gespielten Hauptrundenspiele bis zum Ende dieser Periode.",
        noPeriods: "Für die laufende Saison sind keine Spielperioden hinterlegt.",
        noActiveSeason: "Keine laufende Saison.",
        noTeams: "Keine Teams mit GM.",
        legend: {
          belowMin: "Mindestanzahl nicht erreicht",
          minReached: "Mindestanzahl erreicht",
          complete: "Alle Spiele absolviert"
        },
        cols: {
          team: "Team",
          gm: "GM",
          gamesPlayed: "Spiele",
          toMin: "Bis Minimum",
          toMax: "Noch möglich"
        }
      },

      // Former Champions page (German)
      formerChampions: {
        title: "Ehemalige Champions Ruhmeshalle",
        description: "Ehrung der Legenden, die unsere Liga über die Jahre dominiert haben. Diese Champions haben sich ihren Platz in der Geschichte durch Können, Hingabe und Meisterschaftsruhm verdient.",
        activeGM: "Aktiver GM",
        statsTitle: "Meisterschafts-Statistiken",
        totalChampions: "Gesamt Champions",
        activeChampions: "Noch aktiv",
        totalTitles: "Gesamt Titel",
        mostTitles: "Meiste Titel",
        legendary: "Legendäre Champions (5+ Titel)",
        elite: "Elite Champions (2-4 Titel)",
        oneTime: "Champions (1 Titel)",
        legendTitle: "Legende",
        activeIndicator: "Aktiver GM - Klicken für Profil",
        legendaryIcon: "Legendäre Champions (5+ Titel)",
        eliteIcon: "Elite Champions (2-4 Titel)",
        championIcon: "Champions (1 Titel)"
      },

      // Common terms (German - informal "du")
      common: {
        loading: "Lädt...",
        error: "Fehler",
        noData: "Keine Daten verfügbar",
        viewMore: "Mehr anzeigen",
        back: "Zurück",
        save: "Speichern",
        saving: "Speichern...",
        cancel: "Abbrechen",
        edit: "Bearbeiten",
        delete: "Löschen",
        add: "Hinzufügen",
        search: "Suchen",
        filter: "Filtern",
        season: "Saison",
        game: "Spiel",
        games: "Spiele",
        team: "Team",
        teams: "Teams",
        player: "Spieler",
        players: "Spieler",
        stats: "Statistiken",
        standings: "Tabelle",
        playoffs: "Playoffs",
        champions: "Champions",
        wins: "Siege",
        losses: "Niederlagen",
        winPercentage: "Siege %",
        points: "Punkte",
        rebounds: "Rebounds",
        assists: "Assists",
        steals: "Steals",
        blocks: "Blocks",
        conference: "Conference",
        eastern: "Eastern",
        western: "Western",
        position: "Position",
        overall: "Gesamt",
        badges: "Badges"
      },

      // Home page (German - informal "du")
      home: {
        title: "Willkommen bei der 2K Bundesliga",
        subtitle: "Plattformübergreifende Basketball Liga",
        description: "Xbox Series S/X & PlayStation 5 | NBA 2K26",
        aboutLeague: "Über unsere Liga",
        aboutDescription1: "Willkommen bei der 2K Bundesliga - der führenden plattformübergreifenden NBA 2K Liga! Wir bringen die besten Spieler von Xbox Series S/X und PlayStation 5 zusammen, um mit allen 30 NBA Teams von NBA 2K26 zu konkurieren.",
        aboutDescription2: "Unsere Liga hat zwei Conferences mit je 15 Teams, die eine komplette Saison spielen, in der jedes Team jeden Gegner zweimal trifft. Die besten 6 Teams aus jeder Conference qualifizieren sich für die Playoffs, mit Play-In Turnieren zur Bestimmung der finalen Seeding. Erlebe authentische NBA Action mit echten Kadern von 2kratings.com!",
        recentGames: "Letzte Spiele",
        recentGamesFrom: "Letzte Spiele aus {{seasonName}}",
        topPerformers: "Liga Leaders",
        leagueLeaders: "Liga Leaders",
        currentStandings: "Aktuelle Tabelle", 
        viewFullStandings: "Vollständige Tabelle anzeigen →",
        viewAllGames: "Alle Spiele anzeigen →",
        viewAllStats: "Alle Statistiken anzeigen →",
        liveStreams: "Live Streams",
        noActiveStreams: "Aktuell keine Streams live",
        streamPlaceholder: "Schau später vorbei für Live NBA 2K Streams aus unserer Community!",
        eastStandings: "Eastern Conference",
        westStandings: "Western Conference",
        clickForBoxScore: "Für Statistiken klicken",
        pointsPerGame: "Punkte pro Spiel",
        reboundsPerGame: "Rebounds pro Spiel",
        assistsPerGame: "Assists pro Spiel",
        gmGreeting: "Hallo {{username}}, {{teamName}} Dashboard",
        lastGameResult: "Letztes Spiel",
        noGamesYet: "Noch keine Spiele in dieser Saison",
        seasonOverview: "Saison Übersicht",
        daysToDeadline: "{{days}} Tage übrig",
        deadlineEnds: "Endet {{date}}",
        deadlinePassed: "Abgelaufen",
        gamesLeftDeadline: "Verbleibende Spiele für Deadline",
        gamesLeftSeason: "Verbleibende Spiele diese Saison",
        gamesToMinDeadline: "Spiele bis Deadline-Minimum",
        gamesToMinPlayoffs: "Spiele bis Playoff-Qualifikation",
        gamesPlayed: "Gespielte Spiele",
        unlimited: "Unbegrenzt",
        noLimit: "Kein Limit gesetzt",
        qualified: "Qualifiziert!",
        deadlineMet: "Deadline erfüllt!",
        currentDeadline: "Aktuelle Deadline",
        nextDeadline: "Nächste Deadline",
        noDeadlines: "Keine Deadlines gesetzt",
        vsTeam: "vs",
        win: "S",
        loss: "N",
        viewBoxScore: "Box Score anzeigen",
        seasonPassProgress: "Season Pass",
        rewardsProgress: "Belohnungen & Upgrades",
        currentLevelLabel: "Aktuelle Stufe",
        nextLevelLabel: "Nächste Stufe",
        gamesToNextLevel: "Noch {{games}} Spiele",
        rewardLabel: "Belohnung",
        noSeasonPass: "Kein Season Pass konfiguriert",
        tierRewardsHint: "Belohnungen für das Tier deines Teams",
        allLevelsCompleted: "Alle Stufen abgeschlossen!",
        levelNotReached: "Spiele Spiele um deinen Season Pass zu starten",
        // Rookies & Sophs Dashboard
        rookieSophProgress: "Rookies & Sophs",
        noRookieSoph: "Keine Rookies & Sophs Upgrades konfiguriert",
        rookieSophAllCompleted: "Alle Upgrade-Stufen abgeschlossen!",
        rookieSophNotReached: "Spiele Spiele um Rookies & Sophs Upgrades zu verdienen",
        upgradeLabel: "Upgrade",
        limitationLabel: "Einschr\u00e4nkung",
        // Attribute Upgrades Dashboard
        attributeUpgradesProgress: "Attribut-Upgrades",
        noAttributeUpgrades: "Keine Attribut-Upgrades konfiguriert",
        attrTotalPoints: "Gesamte verdiente Attributpunkte",
        attrUpgradesEarned: "Verdiente Upgrades",
        attrNextUpgrade: "N\u00e4chstes Upgrade in {{games}} Spielen",
        attrRule: "Alle {{games}} Spiele \u2192 {{points}} Attributpunkte"
      },

      // All Games page (German)
      allGames: {
        title: "Alle Spiele",
        searchPlaceholder: "Nach Teamname oder Datum suchen...",
        noResults: "Keine Spiele gefunden.",
        noGames: "Noch keine Spiele in dieser Saison gespielt."
      },

      // Footer (German)
      footer: {
        description: "Plattformübergreifende Basketball Liga",
        platforms: "Xbox Series S/X & PlayStation 5 | NBA 2K26",
        rights: "Alle Rechte vorbehalten"
      },

      // About page (German)
      about: {
        title: "Über die 2K Bundesliga",
        mission: "Unsere Mission",
        missionText1: "Die 2K Bundesliga ist eine wettbewerbsfähige plattformübergreifende Basketball Liga, die leidenschaftliche NBA 2K Spieler von Xbox Series S/X und PlayStation 5 Plattformen zusammenbringt. Unsere Mission ist es, eine authentische, wettbewerbsfähige und inklusive Umgebung zu schaffen, in der Spieler ihre Fähigkeiten mit allen 30 NBA Teams zeigen können.",
        missionText2: "Wir streben danach, die Aufregung und Struktur der echten NBA zu replizieren, komplett mit regulären Saisons, Playoffs und Championship-Ruhm. Jedes Spiel zählt, jede Statistik hat Bedeutung, und jeder Champion verdient sich seinen Platz in der Geschichte.",
        leagueStructure: "Liga-Struktur",
        easternConference: "Eastern Conference",
        easternDesc: "15 Teams konkurrieren in der Eastern Conference und kämpfen um Playoff-Plätze und die Chance, den Osten in den NBA 2K Liga Finals zu vertreten.",
        westernConference: "Western Conference", 
        westernDesc: "15 Teams aus der Western Conference kämpfen um die Vorherrschaft, wobei die besten Teams voranschreiten, um den Liga-Champion zu bestimmen.",
        whatMakesUsDifferent: "Was uns unterscheidet",
        crossPlatformPlay: "Plattformübergreifendes Spielen",
        crossPlatformDesc: "Wir vereinen Xbox und PlayStation Spieler in einer wettbewerbsfähigen Liga und überwinden Plattform-Barrieren.",
        authenticExperience: "Authentische NBA Erfahrung", 
        authenticDesc: "Mit echten NBA Teams und Kadern aus NBA 2K26 bieten wir eine authentische professionelle Basketball-Erfahrung.",
        statsTracking: "Umfassende Statistik-Verfolgung",
        statsDesc: "Jeder Punkt, Rebound, Assist und jede Statistik wird aufgezeichnet und gibt Spielern die Anerkennung, die sie verdienen.",
        multipleSeasons: "Mehrere Saisons",
        multipleSeasonsDesc: "Wir spielen normalerweise 2-3 Saisons pro NBA 2K Version und geben allen mehrere Chancen, um den Titel zu kämpfen.",
        joinOurLeague: "Tritt unserer Liga bei",
        joinDescription: "Interessiert am Wettkampf? Wir begrüßen talentierte Spieler, die sich zu fairem Spiel, Respekt und wettbewerbsfähigem Basketball verpflichten. Kontaktiere uns, um über Team-Besitzer-Möglichkeiten für kommende Saisons zu erfahren.",
        nbaTeamsCount: "NBA Teams",
        gamesPerSeason: "Spiele pro Saison",
        seasonsPerYear: "Saisons pro Jahr",
        format: "Liga-Format",
        teams: "30 NBA Teams aufgeteilt in 2 Conferences (Eastern & Western)",
        regularSeason: "Reguläre Saison: Jedes Team spielt jeden Gegner zweimal (58 Spiele insgesamt)",
        rosters: "Kader: Offizielle NBA 2K26 Kader von www.2kratings.com",
        platform: "Plattform: Plattformübergreifendes Spiel auf Xbox Series S/X und PlayStation 5"
      },

      // Rules page (German)  
      rules: {
        title: "Regelwerk",
        
        // I. Präambel
        preamble: "I. Präambel",
        preambleText: "Spielt fair und versucht möglichst realitätsnah an der NBA ein Spiel aufzubauen. Dazu gehört folgendes: Struktur in der Offense. Versucht nicht nach dem Korberfolg Fast Break zu spielen, installiert Setplays in eure Offense und versucht nicht jeden Angriff mit einem Dunk abzuschließen, sondern spielt ein variables, überlegtes \"simulationsartiges\" Spiel. Jeder User hat sicherlich seinen eigenen Spielstil, bitte respektiert das. Denkt dran, wir sind eine Community in der jeder Teilnehmer eine Meinung hat und diese auch preisgeben darf. Seid freundlich zueinander und sorgt damit für einen reibungslosen Ablauf unserer Liga.",

        // II. Fairness und Verhalten/Spielweise
        fairnessBehavior: "II. Fairness und Verhalten/Spielweise",
        
        fastBreaks: "Fast Breaks",
        fastBreaksText: "Das Wichtigste vorweg: wir wollen kein Run & Gun Geballere! 🙂 7 secs or less als durchgängige Spielphilosophie solltet ihr bitte aus eurem Kopf bannen. 🙂 Ihr dürft zu jeder Zeit einen Fastbreak laufen nach: Steals oder Blocks. Natürlich dürft ihr auch immer mal wieder schnell abschließen, wenn der Gegner auf offensiv Rebounds geht oder nicht zurückläuft. Aber bitte seht davon ab, nach einem Einwurf direkt einen Fastbreak zu laufen. Natürlich ist es was anderes, wenn ihr in den letzten Minuten seid und einen realistischen Rückstand aufholen wollt.",
        
        noQuits: "Keine Quits!",
        noQuitsText: "Es ist zu keiner Zeit gestattet, ein Spiel vorzeitig zu verlassen. Wenn ihr aussichtslos zurückliegt, versucht langsam zu spielen, um den Schaden zu begrenzen, anstatt noch höher unter zu gehen. Lauft eure Systeme und versucht es schon einmal für das nächste Spiel zu üben und die Laufwege zu lernen. Das Werfen von der Mittellinie und Controller-Weglegen wird nicht geduldet. Spielt ordentlich zu Ende, sonst kann es schnell euer letztes Spiel gewesen sein.",
        
        blowoutGames: "Spiele mit deutlichem Vorsprung",
        blowoutGamesText: "Wenn das Spiel entschieden ist, gebt eurem Gegner die Chance das Ergebnis etwas erfreulicher zu gestalten, um ihm nicht den Spielspaß zu nehmen. Natürlich muss dieser dann auch die Einladung annehmen und nicht aus Frust jeden Ball einfach nach vorne schmeißen. Daher: • Starter auswechseln, jungen Bankspielern die Chance geben sich zu beweisen • Keine unnötigen Reaching-Fouls • Kein Run and Gun • Den letzten Angriff ausspielen und die Uhr herunterlaufen lassen • Versucht andere Verteidigungsformen aus",
        
        disconnects: "Disconnects",
        disconnectsText: "Bei einem Disconnect des zurückliegenden Teams bei mehr als 20 Punkten Rückstand im 3. oder mehr als 10 im 4. Viertel, sowie – unabhängig vom Rückstand – in den letzten zwei Minuten des 4. Viertels oder der Overtime, wird das Spiel für das führende Team gewertet. Bei einem Disconnect des führenden Teams wird das Spiel wiederholt. Sonderfälle liegen im Ermessen der Admins!",
        
        inbounds: "Einwürfe",
        inboundsText: "Einwürfe dürfen grundsätzlich abgefangen werden, außer nach Korberfolgen von der Grundlinie und nicht durch den direkten Verteidiger des Einwerfers. In den letzten zwei Minuten (4. VT) tritt diese Einschränkung außer Kraft. Wenn es doch mal passiert, dass ein Inbound-Pass vom direkten Verteidiger abgefangen wird, gebt den Ball dem Gegner zurück.",
        
        intentionalFouls: "Absichtliche Fouls",
        intentionalFoulsText: "Vor den letzten zwei Minuten eines Spiels sind maximal 3 absichtliche Fouls (mit dem Select Button) erlaubt; danach gibt es keine Begrenzung. Sie sind dazu da den Fastbreak zu stoppen oder ein Miss-Match zu beenden. Kurz vor Ende soll darauf geachtet werden, sich foulen zu lassen. Genauso wie bei +3 nicht extra foulen.",
        
        pausingStats: "Pausieren & Statistiken",
        pausingStatsText: "Jeder Spieler darf pro Spiel 5 Mal auf Pause drücken um Statistiken (z.B. Anzahl der Würfe) zu checken. Wenn jemand etwas erledigen und ca. 5 Minuten Spielstopp benötigt, kündigt es bitte vorher über den Voice-Chat an. Der Gegner hat dies im Rahmen zu akzeptieren. Bitte unterlasst das Start-Drücken bei sehr frustrierenden Aktionen.",

        // III. Vorgaben
        requirements: "III. Vorgaben",
        
        numberOfGames: "Anzahl der Spiele",
        numberOfGamesText: "In jeder Saison gibt es feste Spiel Ober- und Untergrenzen, damit die Differenz zwischen den Teams möglichst klein gehalten wird. Wenn ihr einen Grund habt, dass ihr die Spiele nicht erreichen könnt (Urlaub, Schule/Uni, Arbeit etc.), dann sagt uns bitte Bescheid. Es besteht in Einzelfällen die Möglichkeit, dass ein Nutzer \"vorspielen\" darf – wenn er bspw. 3 Wochen in den Urlaub fährt. So etwas ist vorher mit dem Admin-Team abzusprechen und dementsprechend anzukündigen.",
        
        shotLimitation: "Begrenzung der Würfe",
        shotLimitationText: "Ihr sollt bitte immer im Kopf haben, dass Basketball ein Teamsport ist und deshalb der Ball gerne auch geteilt werden sollte. Natürlich sollt ihr auch die Stärken eurer Spieler/eures Teams einsetzen aber keiner möchte Statistiken haben mit 40 Würfe ein Spieler, der zweite zehn und der dritte fünf und der Rest null. Achtet bitte darauf, dass ihr mitverantwortlich seid, wie angenehm eine 2K Runde mit euch sein wird! Der Liga Admin wird bei auffälliger Spielweise (negativ) einschreiten und ggf. Spieler sperren/GM sanktionieren.",
        
        lockOnDefense: "Lock-On-Defense",
        lockOnDefenseText: "On-Ball-Defense ist Pflicht. Wir wollen hier Spieler gegen Spieler spielen und nicht Spieler gegen KI. Ihr könnt versuchen durch kurzes Anwählen eines anderen Spielers eure Verteidigung zu adjustieren (z. B. gegen ein bestimmtes System oder Miss-Match). Nutzt auch die Möglichkeiten der Zonenverteidigung, wobei auch hier darauf geachtet werden muss, dass der ballverteidigende Spieler vom nächsten Verteidiger angewählt ist. Das Adjustieren soll nicht ausgereizt werden und wirklich nur in nötigen Situationen versucht werden. Eine Verteidigung abseits des Ballführers ist verboten.",
        
        pointsInZone: "Punkte in der Zone",
        pointsInZoneText: "Jedes Team soll versuchen, maximal 55% aller Punkte innerhalb der Zone machen (Richtwert). Niedrigere Werte sind ausdrücklich erwünscht. Es werden keine Fastbreak Punkte abgezogen und bei besonders schweren Vergehen behält sich das Adminteam vor ein Spiel zu annullieren oder sogar gegen das regelverstoßende Team zu werten. Ein Überschreiten ist generell kein Ausschluss-Grund, aber versucht euch im Rahmen zu bewegen.",
        
        headsetRequirement: "Headset-Pflicht",
        headsetRequirementText: "Bei allen Spielen gilt Headset-Pflicht. Das soll dazu führen, dass Meinungsverschiedenheiten und andere Probleme schnell und unkompliziert geklärt werden können und daher weniger Diskussionen vor und nach den Spielen stattfinden. Wenn ihr in dem Spiel nicht reden könnt (Frau oder Kinder schlafen), setzt das Headset trotzdem auf und nutzt es in Fällen, wo es Unklarheiten gibt. Ist das Headset defekt, besorgt euch schnellstmöglich Ersatz. In dem Fall macht erst danach wieder Spiele.",
        
        media: "Media",
        mediaText: "Die Community freut sich über jeden Team-Bericht. Es wäre wünschenswert wenn jeder GM während der Saison entweger einen kleinen Blog führt oder hin udn wieder Berichte postet. Während der Play Offs schreibt zu jedem Spiel der Sieger einen Bericht/ lädt die Stats hoch. Highlights bitte auch aufnehmen und im entsprechenden Highlight Thread posten!",

        // IV. Tabellenplatz, Play In & Playoffs Platzierung und Lottery
        standingsPlayoffs: "IV. Tabellenplatz, Play In & Playoffs Platzierung und Lottery",
        
        endOfSeason: "4.1 Am Ende der regulären Saison entsteht eine Tabellenplatzierung. Je nach Platzierung erhöhen sich die Wahrscheinlichkeiten für die nächste Draft.",
        
        playoffQualification: "4.2 Qualifikation für die Playoffs",
        playoffQualificationText: "Wer die Mindestanzahl an Spielen hat, qualifiziert sich für die Playoffs- sofern die Plätze frei sind. Die Platzierung setzt sich nach folgenden Kategorien zusammen: Anzahl der Siege, Anzahl der Spiele, direkter Vergleich",
        
        playInTournament: "4.3 Play In Turnier",
        playInTournamentText: "Play In Turnier kann immer stattfinden, sofern wir zehn Mannschaften im Osten als auch im Westen haben, die die Spielanzahl erreicht haben. Es wird auch Ausnahmen geben, wor wir das Play In Turnier per Voting auch vor einer Saison rausnehmen werden.",
        
        lottery: "4.4 Lottery",
        lotteryText: "Seit Season 33 haben wir eine Lottery die an die der echten NBA angelehnt ist. Mit umgedrehten Record landen die ersten 14 Teams in der Lottery. Sollten Pick 1-4 nicht unter den ersten vier Teams landen, dann werden sie automatisch ab Platz 5 eingereiht. T4 Teams, die nicht in der Lottery landen, werden automatisch ab Pick 15/ nach der Lottery eingereiht. Bei der 1. Draft eines 2K Teils haben alle Teilnehmer dieselbe Wahrscheinlichkeit. Ab der 2. Draft eines 2K Teils kommen die Platzierungen sowie Wahrscheinlichkeiten ins Spiel.",

        // V. Trades & Roster-Management
        tradesRosterManagement: "V. Trades & Roster-Management",
        tradesIntro: "Dies sind die allgemeinen Traderegeln, wie sie üblicherweise für eine Saison gelten. Für die jeweils aktuelle Saison können (auch kurzfristige) Abweichungen gelten. Wir spielen ohne Cap!",
        
        roster: "5.1 Roster",
        rosterText: "Wir starten mit dem Team Rostern von der Draft (Draftday gilt). Jedes Team bestimmt einen Can't Cut Spieler und macht diesen mit der Veröffentlichung der Tradethreads kenntlich. Dieser muss einer aus den Top 3 der OVR sein. Diese Spieler dürfen nicht getradet werden und müssen vor dem 1. Trade festgelegt werden. Ausnahme sind Fantasy-Seasons.",
        
        trades: "5.2 Trades",
        tradesText: "Bis auf den Can't Cut Spieler kann jeder Spieler getradet werden. Es gibt jedoch einige Einschränkungen. Bei einem Trade mit gleicher Spielanzahl darf maximal ein +-3 OVR Unterschied herauskommen. Can't Cut Spieler können im Laufe der Saison wechseln.",
        
        unequalTrades: "5.2.1 Ungleiche Anzahl an Spieler -Trades",
        unequalTradesText: "Falls man einen Spieler für zwei traden möchte/ es eine ungerade Anzahl gibt (3:2 etc.), gilt folgende Regel: Man addiert die Summe aller Spieler und teilt die Summe durch die Anzahl der Spieler. Der OVR Unterschied bei Trades mit ungleicher Spieleranzahl darf nicht mehr als +-3 betragen.",
        
        tradeStaggering: "5.3 Tradestaffelung",
        tradeStaggeringText: "In der Season greift der Seasonpass. Dort wird auch die Tradestaffelung geregelt. Ihr könnt dies unter ⁠nepoznato nachsehen. Bei der Spieleranzahl zählt immer die Anzahl an verschifften Spielern- nicht aufgenommen.",
        
        tradeValidity: "5.4 Gültigkeit eines Trades",
        tradeValidityText: "Ein Trade ist nur gültig und darf im Tradethread eingetragen werden, wenn alle Parteien die erforderlichen Kriterien erfüllen (Spielanzahl im Statstool + verfügbare Spieleranzahl). Trades für die Zukunft dürfen nicht eingetragen werden."
      },

      // Team Owners page (German)
      teamOwners: {
        title: "Team-Besitzer Verzeichnis",
        subtitle: "Lerne die Besitzer hinter jedem Team in unserer Liga kennen",
        loadingOwners: "Lade Team-Besitzer...",
        totalTeamOwners: "Gesamt Team-Besitzer",
        streamersOnTwitch: "Streamer auf Twitch", 
        totalGamesPlayed: "Gesamt gespielte Spiele",
        team: "TEAM",
        gamertag: "GAMERTAG",
        watchOnTwitch: "AUF TWITCH SCHAUEN",
        record: "BILANZ",
        winPercentage: "SIEGE %",
        joined: "Beigetreten:",
        teamAssigned: "Team zugewiesen:",
        noTeamOwnersFound: "Keine Team-Besitzer gefunden"
      },

      // Apply page (German)
      apply: {
        title: "Bewerbung für die 2K Bundesliga",
        subtitle: "Fülle die Bewerbung unten aus und wir schauen sie uns schnellstmöglich an",
        becomeTeamOwner: "Werde Team-Besitzer",
        controlNBATeam: "Kontrolliere ein NBA Team",
        controlTeam: "Kontrolliere ein NBA Team",
        xboxPlaystation: "Xbox & PlayStation",
        xboxPlayStation: "Xbox & PlayStation",
        crossPlatformLeague: "Plattformübergreifende Liga",
        crossPlatform: "Plattformübergreifende Liga",
        competeForGlory: "Kämpfe um den Ruhm",
        competeGlory: "Kämpfe um den Ruhm",
        championshipTrophy: "Championship-Trophäe wartet",
        championship: "Championship-Trophäe wartet",
        personalInformation: "Persönliche Informationen",
        personalInfo: "Persönliche Informationen",
        firstName: "Vorname",
        firstNamePlaceholder: "Gib deinen Vornamen ein",
        lastName: "Nachname",
        lastNamePlaceholder: "Gib deinen Nachnamen ein",
        gamingInformation: "Gaming-Informationen",
        gamingInfo: "Gaming-Informationen",
        nbaGamertag: "NBA 2K Gamertag",
        gamertag: "NBA 2K Gamertag",
        gamertagPlaceholder: "Gib deinen Gamertag ein",
        gamingPlatform: "Gaming-Plattform",
        platform: "Gaming-Plattform",
        selectPlatform: "Wähle deine Plattform",
        xboxSeriesLabel: "Xbox Series S/X",
        xboxSeries: "Xbox Series S/X",
        playstationLabel: "PlayStation 5",
        playStation5: "PlayStation 5",
        emailAddress: "E-Mail-Adresse",
        email: "E-Mail-Adresse",
        emailPlaceholder: "john@beispiel.com",
        inviteCodeEmail: "Wir senden deinen Einladungscode an diese E-Mail",
        emailNote: "Wir senden deinen Einladungscode an diese E-Mail",
        teamPreference: "Team-Präferenz",
        preferredTeam: "Bevorzugtes Team (Optional)",
        noPreference: "Keine Präferenz (Admin wird zuweisen)",
        onlyAvailableTeams: "Wähle dein bevorzugtes Team oder lasse es leer für Admin-Zuweisung. Es werden nur verfügbare Teams angezeigt.",
        teamNote: "Wähle dein bevorzugtes Team oder lasse es leer für Admin-Zuweisung. Es werden nur verfügbare Teams angezeigt.",
        additionalInformation: "Zusätzliche Informationen",
        additionalInfo: "Zusätzliche Informationen",
        nbaExperience: "NBA 2K Erfahrung",
        experience: "NBA 2K Erfahrung",
        experiencePlaceholder: "Erzähl uns von deiner NBA 2K Erfahrung, Skill-Level, Jahre gespielt, usw.",
        whyJoin: "Warum möchtest du beitreten?",
        reason: "Warum möchtest du beitreten?",
        reasonPlaceholder: "Was motiviert dich, der 2K Bundesliga beizutreten?",
        weeklyAvailability: "Wöchentliche Verfügbarkeit",
        availability: "Wöchentliche Verfügbarkeit",
        availabilityPlaceholder: "z.B. Wochentag-Abende, Wochenend-Morgen, usw.",
        submitApplication: "Bewerbung senden",
        submittingApplication: "Sende Bewerbung...",
        submitError: "Ein Fehler ist aufgetreten. Bitte versuche es erneut.",
        alreadyHaveCode: "Hast du bereits einen Einladungscode?",
        registerHere: "Hier registrieren",
        applicationSubmitted: "Bewerbung gesendet!",
        thankYou: "Danke für deine Bewerbung zur 2K Bundesliga! Ein Admin wird deine Bewerbung prüfen und dir bald einen Einladungscode per E-Mail senden.",
        whatHappensNext: "Was passiert als nächstes?",
        applicationReceived: "Deine Bewerbung wurde erhalten",
        adminReview: "Ein Admin wird deine Bewerbung prüfen (normalerweise innerhalb von 24-48 Stunden)",
        receiveEmail: "Du erhältst einen Einladungscode per E-Mail",
        completeRegistration: "Verwende den Code, um deine Registrierung abzuschließen",
        useCodeToRegister: "Verwende den Code, um deine Registrierung abzuschließen",
        backToHome: "Zurück zur Startseite",
        browseTeams: "Teams durchsuchen"
      },

      // Admin area - German translations
      admin: {
        panel: "Admin-Panel",
        dashboard: "Dashboard",
        gamesManagement: "Spiele-Verwaltung",
        playoffManagement: "Playoff-Verwaltung",
        playerManagement: "Spieler-Verwaltung",
        rosterManagement: "Kader-Verwaltung",
        tradeManagement: "Trade-Verwaltung",
        teamManagement: "Team-Verwaltung",
        inviteCodes: "Einladungscodes",
        userManagement: "Benutzer-Verwaltung",
        documentation: "Dokumentation",
        settings: "Einstellungen",
        teamSettings: "Team-Einstellungen",
        changePassword: "Passwort ändern",
        logout: "Abmelden",
        backToWebsite: "Zur Website",
        adminDocumentation: "Admin-Dokumentation",
      gmDocumentation: "GM-Dokumentation",
        
        // Admin Dokumentations-Abschnitte
        adminDocs: {
          // Kader: inaktive Spieler (dasselbe Flag, das GMs selbst setzen)
          rosterAdminNote: "GMs setzen das selbst unter „Kader-Verwaltung“. Als Admin wählst du dort oben jedes Team und schaltest für sie — praktisch, wenn zu Saisonbeginn noch deutlich mehr als 15 Spieler im Kader stehen.",
          title: "Admin Dokumentation",
          subtitle: "Vollständiger Leitfaden zur Verwaltung der 2K Bundesliga",
          overview: "Überblick",
          seasons: "Saison-Verwaltung", 
          fantasy: "Fantasy-Saisons",
          fantasyDraft: "Fantasy Draft",
          players: "Spieler-Verwaltung",
          games: "Spiel-Planung",
          ocr: "OCR Screenshots",
          playoffs: "Playoffs-Verwaltung",
          trades: "Trade-Verwaltung",
          invites: "Einladungs-System",
          users: "Benutzer-Verwaltung",
          backup: "Daten-Sicherung",
          troubleshooting: "Problembehandlung",
          
          // Abschnittsinhalte
          overviewTitle: "2K Bundesliga Admin Panel Überblick",
          mainFunctions: "Hauptfunktionen",
          mainFunctionsList: {
            createSeasons: "Saisons erstellen und verwalten (Standard & Fantasy)",
            scheduleGames: "Spiele planen und Ergebnisse eingeben",
            managePlayoffs: "Playoff-Brackets und Serien verwalten",
            handleTrades: "Spieler-Trades und Transfers bearbeiten", 
            inviteOwners: "Neue Team-Besitzer einladen",
            confirmApplications: "Neue GM-Bewerbungen bestätigen"
          },
          typicalFlow: "Typischer Saisonablauf",
          typicalFlowSteps: {
            step1: "Neue Saison erstellen",
            step2: "Spieler-Kader importieren (falls Custom)",
            step3: "Regular Season Spiele planen",
            step4: "Wöchentlich Spielergebnisse eingeben",
            step5: "Playoffs starten wenn bereit",
            step6: "Saison abschließen & Champion küren"
          },
          importantNotes: "Wichtige Hinweise",
          importantNotesList: {
            activateSeason: "Immer eine Saison aktivieren vor Spielplanung",
            onlyOneActive: "Nur eine Saison kann gleichzeitig aktiv sein",
            fantasyRequiresImport: "Fantasy-Saisons benötigen manuellen Roster-Import",
            backupRegularly: "Daten regelmäßig mit dem Backup-System sichern"
          },
          
          // Saison-Verwaltung
          seasonManagementTitle: "Saison-Verwaltung",
          createStandardSeason: "Standard-Saison erstellen",
          seasonSteps: {
            navigate: "Navigiere zur Saison-Verwaltung",
            navigateDesc: "Gehe zu Admin Panel → Saison-Verwaltung",
            createNew: "Klicke \"Neue Saison erstellen\"",
            createNewDesc: "Fülle aus: Name (z.B. \"Saison 2026\"), Jahr, Startdatum, Enddatum",
            selectType: "Wähle Saison-Typ",
            selectTypeDesc: "Wähle \"Standard\" für reguläre NBA-Roster-Saisons",
            configurePlayoffs: "Playoff-Format konfigurieren",
            configurePlayoffsDesc: "Wähle aus: Single Elimination, Best of 3, Best of 5, Best of 7",
            activateSeason: "Saison aktivieren",
            activateSeasonDesc: "Verwende den \"Aktivieren\"-Button um sie zur aktuellen aktiven Saison zu machen"
          },
          seasonActions: "Saison-Aktionen",
          seasonActionsList: {
            activate: "Saison aktivieren",
            activateDesc: "Macht eine Saison zur aktuellen aktiven Saison. Nur eine Saison kann gleichzeitig aktiv sein.",
            complete: "Saison abschließen", 
            completeDesc: "Markiert eine Saison als beendet. Verwende dies nach Playoff-Abschluss.",
            edit: "Saison bearbeiten",
            editDesc: "Saison-Details wie Name, Daten oder Playoff-Format ändern.",
            delete: "Saison löschen",
            deleteDesc: "Saison komplett entfernen. Verwende \"Force Delete\" für Saisons mit Spielen."
          },

          // Fantasy-Saisons
          fantasySeasonTitle: "Fantasy-Saisons",
          fantasyWhat: "Was sind Fantasy-Saisons?",
          fantasyWhatDesc: "Fantasy-Saisons ermöglichen es, Custom-Ligen mit historischen NBA-Spielern, thematischen Kadern oder komplett eigenen Spielerlisten zu erstellen. Beispiele: \"2000er Ära\", \"90er Legenden\", \"All-Time Greats\".",
          createFantasyTitle: "Fantasy-Saison erstellen",
          fantasySteps: {
            createCustom: "Saison mit Custom-Typ erstellen",
            createCustomDesc: "In Saison-Verwaltung, wähle \"Custom\" als Saison-Typ",
            specifyEra: "Roster-Ära angeben",
            specifyEraDesc: "Gib beschreibende Ära ein (z.B. \"2000er Lakers Ära\", \"90er Bulls Dynastie\")",
            copyTemplate: "CSV-Template kopieren",
            copyTemplateDesc: "Kopiere das angezeigte CSV-Template-Format für deinen Custom-Roster",
            createCSV: "Deine CSV-Datei erstellen",
            createCSVDesc: "Fülle deine Custom-Spieler nach dem Template-Format aus",
            importRoster: "Roster importieren",
            importRosterDesc: "Verwende \"Custom Roster importieren\"-Button um deine CSV-Datei hochzuladen"
          },
          csvTemplateTitle: "CSV-Template-Format",
          requiredFields: "Pflichtfelder:",
          requiredFieldsList: {
            firstName: "Vorname: Vorname des Spielers",
            lastName: "Nachname: Nachname des Spielers", 
            position: "Position: PG, SG, SF, PF, C (oder Kombinationen)",
            overall: "Overall Rating: Zahl 1-99",
            teamName: "Team-Name: Muss mit bestehendem Team übereinstimmen",
            badges: "Badges: Anzahl der Badges (0-20)"
          },
          tips: "Tipps:",
          tipsList: {
            saveCSV: "Datei mit .csv-Erweiterung speichern",
            consistentNames: "Team-Namen konsistent halten",
            ratingsRange: "Ratings typischerweise 75-99",
            badgeCounts: "Badge-Anzahl: 0-6 (Rollenspieler), 7-15 (Stars), 16+ (Superstars)"
          },

          // Fantasy Draft
          fantasyDraftTitle: "Fantasy Draft Verwaltung",
          fantasyDraftDesc: "Fantasy Draft ermöglicht es, Snake-Style Drafts zu erstellen, bei denen GMs abwechselnd Spieler auswählen.",
          fantasyDraftWhat: "Was ist Fantasy Draft?",
          setupFantasyDraft: "Fantasy Draft einrichten",
          fantasyDraftSteps: {
            createSeason: "Erstelle zuerst eine Fantasy-Saison",
            createSeasonDesc: "Gehe zu Saison-Verwaltung → Custom-Saison erstellen → Importiere deinen Spielerpool via CSV",
            navigate: "Navigiere zu Fantasy Draft",
            navigateDesc: "Admin Panel → Fantasy Draft Verwaltung",
            createDraft: "Neuen Fantasy Draft erstellen",
            createDraftDesc: "Wähle deine Fantasy-Saison, setze Runden (typischerweise 13-15 für volle Kader)",
            assignTeams: "Teilnehmende Teams & GMs zuweisen",
            assignTeamsDesc: "Wähle welche Teams teilnehmen und stelle sicher, dass alle zugewiesene GMs haben",
            setOrder: "Draft-Reihenfolge festlegen",
            setOrderDesc: "Wähle zufällige Reihenfolge oder setze manuelle Reihenfolge für erste Runde",
            startDraft: "Draft starten",
            startDraftDesc: "Live-Draft beginnen - GMs wählen abwechselnd Spieler in Snake-Reihenfolge"
          },
          liveDraftManagement: "Live Draft Verwaltung",
          draftFeatures: "Draft-Features:",
          draftFeaturesList: {
            snakeOrder: "Snake-Reihenfolge: Draft-Reihenfolge kehrt sich jede Runde um",
            liveUpdates: "Live-Updates: Echtzeit Draft-Board für alle Teilnehmer",
            timeLimits: "Zeitlimits: Pick-Timer setzen (Auto-Pick verfügbar)",
            playerFiltering: "Spieler-Filter: Filter nach Position, Rating, Team",
            draftHistory: "Draft-Historie: Alle Picks in Echtzeit verfolgen"
          },
          adminControls: "Admin-Kontrollen:",
          adminControlsList: {
            forcePick: "Pick erzwingen: Pick für abwesenden GM machen",
            undoPick: "Pick rückgängig: Fehler-Picks umkehren",
            pauseResume: "Pausieren/Fortsetzen: Draft-Timing kontrollieren",
            skipPick: "Pick überspringen: Zum nächsten GM wechseln falls nötig",
            completeDraft: "Draft abschließen: Finalisieren und Kader zuweisen"
          },

          // OCR
          ocrTitle: "OCR Screenshot Upload",
          ocrDesc: "Optische Zeichenerkennung (OCR) ermöglicht die automatische Extraktion von Spielerstatistiken aus NBA 2K Screenshots.",
          ocrOverview: "OCR Überblick",
          takingScreenshots: "Screenshots erstellen",
          bestPractices: "Beste Praktiken:",
          bestPracticesList: {
            highRes: "Verwende hohe Auflösung (1080p+)",
            goodLighting: "Stelle gute Beleuchtung/Kontrast sicher",
            keepStraight: "Halte den Bildschirm gerade (keine Winkel)",
            zoomStats: "Zoome an Stats-Tabelle heran",
            cropUI: "Schneide extra UI-Elemente ab",
            separateShots: "Mache separate Aufnahmen für jedes Team"
          },
          avoid: "Vermeide:",
          avoidList: {
            blurry: "Unscharfe oder niedrig aufgelöste Bilder",
            glare: "Screenshots mit Blendung oder Schatten",
            rotated: "Gedrehte oder verzerrte Bilder", 
            extraUI: "Zu viel extra UI enthalten",
            differentFormats: "Verschiedene Formate zwischen Spielen",
            missingData: "Fehlende Spielernamen oder Stats"
          },
          usingOCR: "OCR für Spielstatistiken verwenden",
          ocrSteps: {
            navigate: "Navigiere zur Spieleingabe", 
            navigateDesc: "Spiele-Verwaltung → Finde dein Spiel → Klicke \"Stats eingeben\"",
            selectTab: "Wähle OCR-Tab",
            selectTabDesc: "Klicke \"📸 Screenshots hochladen\"-Tab im Stats-Eingabe-Modal",
            uploadScreenshots: "Lade zwei Screenshots hoch",
            uploadScreenshotsDesc: "Lade Heimteam-Screenshot und Auswärtsteam-Screenshot hoch",
            extractStats: "Stats extrahieren",
            extractStatsDesc: "Klicke \"Stats extrahieren\" - Verarbeitung dauert 10-30 Sekunden",
            reviewCorrect: "Überprüfen & korrigieren",
            reviewCorrectDesc: "Prüfe extrahierte Daten, korrigiere Fehler, löse Spielername-Konflikte",
            submit: "Abschicken",
            submitDesc: "Klicke \"Score & Stats abschicken\" um alle Daten zu speichern"
          },
          troubleshootingOCR: "OCR-Fehlerbehebung",
          playerNameConflicts: "Spielername-Konflikte",
          playerNameConflictsDesc: "Falls mehrere Spieler ähnliche Namen haben (z.B. \"A. Smith\"), siehst du ein Dropdown:",
          playerNameConflictsList: {
            reviewStats: "Überprüfe die extrahierten Stats um den richtigen Spieler zu bestimmen",
            selectPlayer: "Wähle den richtigen Spieler aus dem Dropdown",
            systemRemembers: "System merkt sich deine Wahl für zukünftige Spiele"
          },
          statValidation: "Stat-Validierungs-Warnungen",
          statValidationDesc: "System validiert automatisch extrahierte Stats:",
          statValidationList: {
            minutes: "Minuten > 48: Meist ungültig, prüfe Screenshot",
            madeAttempted: "Getroffen > Versucht: Korrigiere Wurfstatistiken",
            pointsMismatch: "Punkte-Abweichung: Überprüfe Punkte-Berechnung",
            invalidRanges: "Ungültige Bereiche: Prüfe Stat-Plausibilität"
          },
          accuracyTips: "Genauigkeits-Tipps",
          accuracyTipsList: {
            alwaysReview: "Überprüfe immer extrahierte Daten vor dem Abschicken",
            consistentQuality: "OCR funktioniert am besten mit konsistenter Screenshot-Qualität",
            manualCorrection: "Manuelle Korrektur ist normal für komplexe Screenshots",
            saveTime: "Spare Zeit durch OCR für Bulk-Daten, korrigiere Details manuell"
          },

          // Backup
          backupTitle: "Daten-Sicherung & Wiederherstellung",
          backupDesc: "Regelmäßige Backups sind entscheidend zum Schutz Ihrer Liga-Daten.",
          backupImportance: "Backup-Wichtigkeit",
          creatingBackups: "Backups erstellen",
          backupSteps: {
            navigate: "Navigiere zum Backup-Verzeichnis",
            navigateDesc: "In deinem Projekt-Root, gehe zum \"scripts\"-Ordner",
            runScript: "Backup-Script ausführen",
            runScriptDesc: "node scripts/backup-database.js",
            backupLocation: "Backup-Standort",
            backupLocationDesc: "Backups werden im \"backups/\"-Ordner mit Zeitstempel-Namen gespeichert",
            quickBackup: "Schnelle Backup-Option",
            quickBackupDesc: "Verwende node scripts/quick-backup.js für schnellere Backups"
          },
          backupSchedule: "Backup-Zeitplan",
          regularSchedule: "Regelmäßiger Zeitplan:",
          regularScheduleList: {
            weekly: "Wöchentlich: Jeden Sonntag vor Spielen", 
            beforeOperations: "Vor Operationen: Saison-Erstellung, CSV-Importe",
            playoffTime: "Playoff-Zeit: Vor jeder Playoff-Runde",
            tradeDeadline: "Trade-Deadline: Vor Verarbeitung von Bulk-Trades"
          },
          quickBackups: "Schnelle Backups:",
          quickBackupsList: {
            beforeAdmin: "Vor Admin-Panel-Operationen",
            afterEntry: "Nach großen Dateneingabe-Sitzungen",
            testingFeatures: "Beim Testen neuer Features",
            roleChanges: "Vor Benutzerrollen-Änderungen"
          },
          restoringBackup: "Aus Backup wiederherstellen",
          restorationWarning: "Warnung: Wiederherstellung überschreibt aktuelle Daten. Mit äußerster Vorsicht verwenden!",
          restorationSteps: {
            stopOperations: "Stoppe alle Operationen",
            stopOperationsDesc: "Stelle sicher, dass keine Benutzer im System aktiv sind",
            chooseBackup: "Wähle Backup-Datei",
            chooseBackupDesc: "Identifiziere den Backup-Ordner mit Zeitstempel, den du wiederherstellen möchtest",
            runRestoration: "Wiederherstellungs-Script ausführen",
            runRestorationDesc: "node scripts/seed-from-backup.js [BACKUP_FOLDER_NAME]",
            verifyRestoration: "Wiederherstellung überprüfen",
            verifyRestorationDesc: "Prüfe, dass alle Daten korrekt wiederhergestellt wurden",
            communicateChanges: "Änderungen kommunizieren",
            communicateChangesDesc: "Informiere alle Benutzer über die Wiederherstellung und eventuelle verlorene Daten"
          },

          // Spieler-Verwaltung
          playerManagementTitle: "Spieler-Verwaltung",
          addingNewPlayers: "Neue Spieler hinzufügen",
          playerSteps: {
            navigate: "Navigiere zur Spieler-Verwaltung",
            navigateDesc: "Admin Panel → Spieler-Verwaltung",
            clickAdd: "Klicke \"Spieler hinzufügen\"",
            clickAddDesc: "Fülle Spieler-Details aus: Name, Position, Rating, Team"
          }
        },

        // Can't Cut Player translations
        cantCutPlayer: "Unhandelbarer Spieler",
        cantCutRules: "Regeln für unhandelbare Spieler",
        cantCutRule1: "Du kannst einen Spieler aus deinen Top 3 Gesamt-Bewertungen als unhandelbar festlegen",
        cantCutRule2: "Dieser Spieler kann nicht getauscht werden und blockiert alle Trades, in denen er enthalten ist",
        cantCutRule3: "Einmal festgelegt, ist die Auswahl deines unhandelbaren Spielers dauerhaft und kann nicht mehr geändert werden",
        cantCutRule4: "Du musst einen unhandelbaren Spieler festlegen, bevor du Trades vorschlagen kannst",
        currentCantCutPlayer: "Aktueller unhandelbarer Spieler",
        selectCantCutPlayer: "Unhandelbaren Spieler auswählen",
        selectNewCantCutPlayer: "Neuen unhandelbaren Spieler auswählen",
        cantCut: "Unhandelbar",
        overall: "Gesamt",
        position: "Position",
        cantCutPlayerUpdated: "Unhandelbarer Spieler wurde erfolgreich aktualisiert",
        errorUpdatingCantCut: "Fehler beim Aktualisieren des unhandelbaren Spielers",
        errorFetchingSeason: "Fehler beim Abrufen der aktuellen Saison",
        errorLoadingPlayers: "Fehler beim Laden der Spieler",
        noEligiblePlayers: "Keine geeigneten Spieler verfügbar",
        saving: "Speichern...",
        setCantCutPlayer: "Unhandelbaren Spieler festlegen",
        selected: "Ausgewählt",
        currentCantCut: "Aktuell unhandelbar",
        clearSelection: "Auswahl löschen",
        pleaseSelectPlayer: "Bitte wählen Sie zuerst einen Spieler aus",
        cantCutAlreadySet: "Ein unhandelbarer Spieler wurde bereits festgelegt und kann nicht geändert werden",
        cantCutConfirmation: "Auswahl des unhandelbaren Spielers bestätigen",
        cantCutWarning: "WARNUNG: Sobald Sie einen unhandelbaren Spieler festlegen, ist diese Auswahl dauerhaft und kann nicht mehr geändert werden. Sind Sie sicher, dass Sie diesen Spieler als Ihren unhandelbaren Spieler bestimmen möchten?",
        confirmSelection: "Auswahl bestätigen",
        permanentSelection: "Diese Auswahl ist dauerhaft",
        otherSettings: "Weitere Einstellungen",
        moreSettingsComingSoon: "Weitere Einstellungsoptionen werden bald verfügbar sein",
        
        // Spieler-Verwaltung
        playerManagementTitle: "Spieler-Verwaltung",
        allTeams: "Alle Teams",
        searchPlayers: "Spieler suchen...",
        
        // Team-Verwaltung
        teamName: "Team-Name",
        
        // GM Dokumentations-Abschnitte
        gmDocs: {
          title: "GM Dokumentation",
          subtitle: "Vollständiger Leitfaden für Team-Besitzer und General Manager",
          overview: "Überblick",
          profile: "Profil-Verwaltung",
          team: "Team-Verwaltung", 
          trades: "Trade-Vorschläge",
          games: "Spiel-Ergebnisse",
          schedule: "Mein Spielplan",
          playoffs: "Playoff-Spiele",
          settings: "Einstellungen",
          
          // Abschnittsinhalte
          overviewTitle: "Team-Besitzer Leitfaden",
          yourResponsibilities: "Deine Verantwortlichkeiten",
          responsibilitiesList: {
            manageRoster: "Verwalte dein Team-Roster und Strategie",
            enterResults: "Gib Spielergebnisse und Spielerstatistiken ein",
            proposeTrades: "Schlage Trades mit anderen Teams vor",
            updateProfile: "Halte deine Profil-Informationen aktuell",
            followRules: "Befolge Liga-Regeln und Fristen",
            participatePlayoffs: "Nimm an Playoff-Spielen teil wenn qualifiziert"
          },
          regularTasks: "Regelmäßige Aufgaben",
          regularTasksList: {
            checkSchedule: "Prüfe deinen wöchentlichen Spielplan",
            playGames: "Spiele geplante Spiele mit Gegnern",
            enterResults: "Gib Spielergebnisse innerhalb der Frist ein",
            reviewTrades: "Überprüfe Trade-Vorschläge regelmäßig",
            monitorStandings: "Überwache Liga-Tabelle und Nachrichten",
            updateGamertag: "Aktualisiere deinen Gamertag falls nötig"
          },
          gettingHelp: "Hilfe bekommen",
          gettingHelpList: {
            contactAdmins: "Kontaktiere Liga-Admins bei technischen Problemen",
            checkDocs: "Prüfe diese Dokumentation für Anleitungen",
            askGMs: "Frage andere GMs im Liga-Chat nach Tipps",
            reportBugs: "Melde Bugs oder Probleme umgehend"
          },

          profileTitle: "Profil-Verwaltung",
          profileDesc: "Verwalten Sie Ihr persönliches Profil, Gamertag und Streaming-Informationen.",
          profileOverview: "Profil-Überblick",
          profileOverviewDesc: "Dies hilft anderen Spielern, dich zu finden und zu kontaktieren.",
          updatingProfile: "Profil aktualisieren",
          profileSteps: {
            navigate: "Navigiere zu Einstellungen",
            navigateDesc: "Klicke deinen Benutzernamen oben rechts → Einstellungen",
            updateInfo: "Profil-Informationen aktualisieren",
            updateInfoDesc: "Bearbeite deinen Vornamen, Gamertag, Gaming-Plattform und Twitch-Profil",
            saveChanges: "Änderungen speichern",
            saveChangesDesc: "Klicke \"Profil aktualisieren\" um deine Änderungen zu speichern"
          },

          teamTitle: "Team-Verwaltung", 
          teamDesc: "Betrachten Sie Ihren Kader, Spielerstatistiken und Team-Informationen.",
          teamManagement: "Team-Verwaltung",
          teamManagementDesc: "Verfolge deine Spieler und Team-Leistung.",
          viewingTeam: "Dein Team betrachten",
          teamSteps: {
            navigate: "Navigiere zur Teams-Seite",
            navigateDesc: "Hauptnavigation → Teams → Finde dein Team",  
            clickTeam: "Klicke deine Team-Karte",
            clickTeamDesc: "Dies öffnet die Roster-Seite deines Teams",
            reviewRoster: "Roster überprüfen",
            reviewRosterDesc: "Sieh alle deine Spieler, ihre Ratings und Positionen"
          },
          playerInformation: "Spieler-Informationen",
          rosterPageShows: "Roster-Seite zeigt:",
          rosterShowsList: {
            playerNames: "Spielernamen: Vollständige Namen deiner Spieler",
            positions: "Positionen: PG, SG, SF, PF, C",
            overallRating: "Overall Rating: Spieler-Skill-Level (0-99)",
            badgeCount: "Badge-Anzahl: Anzahl der Badges die der Spieler hat",
            activeStatus: "Aktiv-Status: steuerst du in der Kader-Verwaltung, nicht hier"
          },
          managingPlayers: "Spieler verwalten:",
          managingPlayersList: {
            activeInactive: "Aktiv/Inaktiv: im Admin-Menü unter „Kader-Verwaltung“ umschalten",
            viewStats: "Stats anzeigen: Klicke Spieler um Saison-Stats zu sehen",
            tradeConsideration: "Trade-Überlegung: Trade-Kandidaten identifizieren",
            lineupPlanning: "Aufstellung planen: Plane deine Spiel-Rotationen"
          },
          teamPerformance: "Team-Leistung",
          trackingRecord: "Deine Bilanz verfolgen:",
          trackingRecordList: {
            checkStandings: "Prüfe die Tabellen-Seite für deine aktuelle Bilanz",
            viewRecord: "Sieh Siege, Niederlagen und Siegprozentsatz",
            seeRanking: "Sieh deine Conference-Platzierung",
            monitorPlayoffs: "Überwache Playoff-Positionierung"
          },
          seasonGoals: "Saison-Ziele:",
          seasonGoalsList: {
            aimPlayoffs: "Strebe Playoff-Qualifikation an (meist Top 8)",
            buildChemistry: "Baue Team-Chemie durch konsistente Aufstellungen auf",
            developPlayers: "Entwickle die Fähigkeiten deiner Spieler über die Saison",
            makeTrades: "Mache strategische Trades um schwache Bereiche zu verbessern"
          },

          tradesTitle: "Trade-Vorschläge",
          tradesDesc: "Schlagen Sie Trades mit anderen Teams vor und verwalten Sie Trade-Angebote.",
          tradeSystem: "Trade-System",
          tradeSystemDesc: "Alle Trades benötigen Admin-Genehmigung bevor sie wirksam werden.",
          proposingTrade: "Einen Trade vorschlagen",
          tradeSteps: {
            navigate: "Navigiere zu Meine Trades",
            navigateDesc: "Admin Dashboard → Meine Trades (Team-Besitzer Bereich)",
            clickPropose: "Klicke \"Neuen Trade vorschlagen\"",
            clickProposeDesc: "Dies öffnet das Trade-Vorschlag-Formular",
            selectPartner: "Wähle Trading-Partner",
            selectPartnerDesc: "Wähle mit welchem Team du handeln möchtest",
            choosePlayers: "Wähle Spieler",
            choosePlayersDesc: "Wähle Spieler die du anbietest und Spieler die du willst",
            addMessage: "Trade-Nachricht hinzufügen",
            addMessageDesc: "Erkläre warum dieser Trade beiden Teams nützt",
            submitProposal: "Vorschlag abschicken",
            submitProposalDesc: "Schicke den Trade zur Admin-Überprüfung"
          },

          gamesTitle: "Spiel-Ergebnis-Eingabe",
          gamesDesc: "Geben Sie Punkte und Statistiken für die Spiele Ihres Teams ein.",

          // Kaderverwaltung: GMs setzen Spieler, die sie nicht einsetzen, auf inaktiv
          rosterTitle: "Kader: aktive und inaktive Spieler",
          rosterIntro: "Dein 2K-Kader hat meist mehr als 15 Spieler, eingesetzt wird aber nur ein Teil davon. Setze den Rest auf inaktiv — sie bleiben in deinem Kader und behalten ihre Historie, verschwinden aber aus allem, was die Liga auswertet.",
          rosterWhere: "Wo du das machst",
          rosterWhereSteps: {
            open: "Öffne links im Admin-Menü „Kader-Verwaltung“",
            toggle: "Klicke beim Spieler auf „✕ Deaktivieren“",
            undo: "Inaktive rutschen in den Block ganz unten — „✓ Aktivieren“ holt sie jederzeit zurück"
          },
          rosterEffect: "Was inaktiv bedeutet",
          rosterEffectList: {
            hidden: "Weg von deiner öffentlichen Team-Seite, aus der Spielerliste und aus dem Spielervergleich",
            leaders: "Weg aus den Bestenlisten und den Award-Stat-Leadern — auch mit bereits erfassten Stats",
            entry: "Weg aus der Statistikerfassung; der Schalter „Inaktive Spieler anzeigen“ holt sie zurück, falls doch einer gespielt hat",
            kept: "Boxscores, Team-Summen und die Spielerseite bleiben unverändert — gelöscht wird nichts",
            visible: "Sichtbar nur noch im Admin-Bereich: auf dieser Seite und in der Spieler-Verwaltung"
          },
          rosterNotes: "Gut zu wissen",
          rosterNotesList: {
            noLimit: "Kein hartes Limit — der Zähler wird ab mehr als 15 aktiven Spielern nur orange",
            anytime: "Du kannst jederzeit umschalten, auch mitten in der Saison",
            reset: "Nach Trade, Entlassung oder Neuzuweisung kommt der Spieler wieder aktiv an",
            perSeason: "Der Status gehört zur Saison — vergangene Saisons behalten ihren eigenen"
          }
        },
        
        // Dashboard-Titel und Beschreibungen  
        superAdminDashboard: "Super Admin Dashboard",
        teamOwnerDashboard: "Team-Besitzer Dashboard",
        welcomeBack: "Willkommen zurück, {{username}}!",
        adminWelcomeText: "Verwalte deine NBA 2K Bundesliga von diesem Admin Panel aus.",
        ownerWelcomeText: "Du verwaltest {{teamName}}.",
        managingTeam: "Verwaltet: {{teamName}}",
        
        // Stats cards
        totalTeams: "Gesamt Teams",
        totalPlayers: "Gesamt Spieler",
        activeSeason: "Aktive Saison",
        totalUsers: "Gesamt Benutzer",
        
        // Quick Actions widgets
        quickActions: "Schnellzugriffe",
        createNewSeasons: "Neue Saisons erstellen, aktuelle Saison-Einstellungen verwalten",
        scheduleGames: "Spiele planen, Ergebnisse eingeben, alle Spiele anzeigen",
        managePlayerRosters: "Spieler-Kader, Bewertungen und Statistiken verwalten",
        reviewTrades: "Trade-Vorschläge prüfen und genehmigen/ablehnen", 
        manageTeamsAssign: "Teams verwalten, Besitzer zuweisen, Team-Infos aktualisieren",
        manageUserAccounts: "Benutzerkonten, Rollen und Berechtigungen verwalten",
        reviewApplications: "Bewerbungen prüfen, Einladungscodes an neue Besitzer senden",
        viewScheduleResults: "Spielplan anzeigen, Spielergebnisse eingeben",
        proposeTradesHistory: "Trades vorschlagen, Trade-Historie anzeigen",
        updateTeamInfo: "Team-Informationen und Einstellungen aktualisieren",
        
        // Owner-specific labels
        myGames: "Meine Spiele",
        myTrades: "Meine Trades",
        teamSettings: "Team-Einstellungen",
        
        // Recent Activity
        recentActivity: "Letzte Aktivitäten",
        noRecentActivity: "Keine letzten Aktivitäten anzuzeigen.",
        
        // Common admin actions
        create: "Erstellen",  
        update: "Aktualisieren",
        assign: "Zuweisen",
        unassign: "Entfernen",
        approve: "Genehmigen",
        reject: "Ablehnen",
        activate: "Aktivieren",
        deactivate: "Deaktivieren",
        reset: "Zurücksetzen", 
        generate: "Generieren",
        import: "Importieren",
        export: "Exportieren",
        
        // Season Management (nested to match English structure)
        seasonManagement: {
          title: "Saison-Verwaltung",
          currentSeason: "Aktuelle Saison",
          seasonName: "Saison-Name",
          seasonType: "Saison-Typ",
          regularSeason: "Reguläre Saison",
          customSeason: "Custom Saison", 
          fantasyDraft: "Fantasy Draft",
          startDate: "Startdatum",
          endDate: "Enddatum",
          isActive: "Aktive Saison",
          createSeason: "Neue Saison erstellen",
          updateSeason: "Saison aktualisieren",
          activateSeason: "Saison aktivieren",
          deactivateSeason: "Saison deaktivieren",
          seasonCreated: "Saison erfolgreich erstellt",
          seasonUpdated: "Saison erfolgreich aktualisiert", 
          seasonActivated: "Saison erfolgreich aktiviert",
          seasonDeactivated: "Saison erfolgreich deaktiviert",

          // Saison-Setup-Assistent (AP6)
          setupTab: "Setup",
          setupTitle: "Saison-Setup-Checkliste",
          setupDescription: "Live-Status je Setup-Schritt. Jeder Button ist gefahrlos mehrfach ausführbar.",
          setupRefresh: "Aktualisieren",
          setupLoading: "Status wird geladen…",
          setupLoadError: "Setup-Status konnte nicht geladen werden",
          setupDone: "Erledigt",
          setupOpen: "Offen",
          setupMaterializing: "Wird materialisiert…",
          setupMaterializeDone: "Roster materialisiert: {{created}} angelegt, {{skipped}} übersprungen ({{total}} im Standard-Pool).",
          setupMaterializeError: "Roster konnte nicht materialisiert werden",
          setupStepRoster: "Roster materialisiert",
          setupStepRosterStatus: "{{count}} Roster-Einträge für diese Saison",
          setupActionMaterialize: "Roster materialisieren",
          setupStepGames: "Spielplan erzeugt",
          setupStepGamesStatus: "{{total}} Spiele, {{played}} gespielt",
          setupActionGoGames: "Spiele öffnen",
          setupStepDeadlines: "Deadlines",
          setupStepSeasonPass: "Season Pass",
          setupStepRookieSoph: "Rookies & Sophs",
          setupStepCountStatus: "{{count}} konfiguriert",
          setupActionGoTab: "Zum Tab",
          setupStepFreeAgency: "Free Agency",
          setupStepFreeAgencyYes: "Für diese Saison konfiguriert",
          setupStepFreeAgencyNo: "Noch nicht eingerichtet",
          setupActionGoFreeAgency: "Free Agency öffnen",
          setupStepGms: "GMs mit Team",
          setupStepGmsStatus: "{{count}} GMs einem Team zugeordnet",
          setupActionGoUsers: "Benutzer öffnen",
          setupStepGmHistory: "GM-Historie gesichert",
          setupStepGmHistoryStatus: "{{count}} GM→Team-Einträge für diese Saison eingefroren",
          setupActionSnapshot: "GM-Historie sichern",
          setupSnapshotting: "Wird gesichert…",
          setupSnapshotDone: "GM-Historie gesichert: {{created}} GM→Team-Einträge eingefroren ({{total}} GMs). Vor dem Neuverteilen der Teams ausführen.",
          setupSnapshotAlready: "GM-Historie für diese Saison bereits gesichert ({{existing}} Einträge); unverändert gelassen.",
          setupSnapshotError: "GM-Historie konnte nicht gesichert werden",

          // Gameplay Settings tab
          generalSettingsTab: "Allgemeine Einstellungen",
          gameplaySettingsTab: "Spieleinstellungen",
          seasonSettings: "Saison-Einstellungen",
          seasonSettingsDescription: "Definiere Grundregeln für Spielanzahl und Playoff-Qualifikation.",
          maxGames: "Maximale Anzahl Spiele",
          maxGamesHelp: "Maximale Anzahl Spiele pro Team in dieser Saison. Leer lassen für kein Limit.",
          minGamesForPlayoffs: "Mindestspiele für Playoffs",
          minGamesForPlayoffsHelp: "Mindestanzahl Spiele, die ein Team für die Playoff-Qualifikation absolvieren muss.",
          maxTradesPerTeam: "Maximale Trades pro Team",
          maxTradesPerTeamHelp: "Trades, die ein Team pro Saison machen darf. Offene Angebote reservieren den Slot bereits. Leer lassen für kein Limit.",
          maxTradedPlayersPerTeam: "Maximal abgegebene Spieler pro Team",
          maxTradedPlayersPerTeamHelp: "Spieler, die ein Team pro Saison abgeben darf, summiert über alle seine Trades. Leer lassen für kein Limit.",
          saveGameplaySettings: "Saison-Einstellungen speichern",
          gameplaySettingsSaved: "Saison-Einstellungen erfolgreich gespeichert",
          noLimit: "Kein Limit",

          // Deadlines
          deadlines: "Fristen",
          deadlinesDescription: "Definiere Meilensteine mit Spielzielen, die Teams bis zu einem bestimmten Datum erreichen müssen.",
          addDeadline: "Frist hinzufügen",
          editDeadline: "Frist bearbeiten",
          saveDeadline: "Frist speichern",
          confirmDeleteDeadline: "Bist du sicher, dass du diese Frist löschen möchtest?",
          noDeadlines: "Noch keine Fristen definiert. Klicke auf \"Frist hinzufügen\".",
          deadlineLabel: "Bezeichnung / Name",
          deadlineLabelPlaceholder: "z.B. Spieltag 1 Deadline",
          deadlineStartDate: "Startdatum",
          deadlineEndDate: "Enddatum",
          deadlineMinGames: "Min. Spiele",
          deadlineMaxGames: "Max. Spiele (optional)",
          deadlineMaxGamesHelp: "Leer lassen für kein oberes Limit.",

          // Season Pass
          seasonPassTab: "Season Pass",
          seasonPass: "Season Pass",
          seasonPassDescription: "Definiere Fortschrittsstufen basierend auf gespielten Spielen. Jede Stufe gewährt eine Belohnung.",
          tierColumn: "Tier-Texte",
          tierEmpty: "{{tier}}: kein eigener Text — greift auf die Belohnungsbeschreibung zurück",
          tierRewards: "Tier-abhängige Belohnungen",
          tierRewardsHint: "Optional. Jeder GM sieht nur den Text seines Team-Tiers. Leere Felder fallen auf die Belohnungsbeschreibung zurück.",
          addLevel: "Stufe hinzufügen",
          editLevel: "Stufe bearbeiten",
          saveLevel: "Stufe speichern",
          confirmDeleteLevel: "Bist du sicher, dass du diese Stufe löschen möchtest? Verbleibende Stufen werden neu nummeriert.",
          noLevels: "Noch keine Season Pass Stufen definiert. Klicke auf \"Stufe hinzufügen\".",
          levelNumber: "Stufe",
          gamesRequired: "Benötigte Spiele",
          rewardDescription: "Belohnung / Überschrift",
          rewardDescriptionPlaceholder: "z.B. Training Camp, Second Chance",
          rewardDescriptionHint: "Wird jedem GM angezeigt, für dessen Tier unten kein eigener Text hinterlegt ist.",
          icon: "Symbol (optional)",
          selectIcon: "Symbol wählen",
          noIcon: "Kein Symbol",
          levelSaved: "Stufe erfolgreich gespeichert",
          levelDeleted: "Stufe gelöscht und Stufen neu nummeriert",
          maxGamesNote: "Saison-Maximum: {{max}} Spiele",
          noMaxGamesNote: "Kein Saison-Spiellimit gesetzt",

          // Rookies & Sophs
          rookieSophTab: "Rookies & Sophs",
          rookieSoph: "Rookies & Sophs Upgrades",
          rookieSophDescription: "Definiere Upgrade-Stufen für Rookies und Sophomores basierend auf gespielten Spielen. Jede Stufe kann eine Einschränkung enthalten.",
          noRookieSophLevels: "Noch keine Rookies & Sophs Stufen definiert. Klicke auf \"Stufe hinzufügen\".",
          upgradeDescription: "Upgrade-Beschreibung",
          upgradeDescriptionPlaceholder: "z.B. +2 Badge-Punkte, Silber zu Gold Upgrade",
          limitationDescription: "Einschränkung",
          limitationDescriptionPlaceholder: "z.B. Nur anwendbar auf Badges unter Gold",

          // Attribute Upgrades
          attributeUpgradesTab: "Attribut-Upgrades",
          attributeUpgrades: "Attribut-Upgrades",
          attributeUpgradesDescription: "Konfiguriere wie Attributpunkte basierend auf gespielten Spielen verdient werden. Punkte werden in regelmäßigen Intervallen vergeben.",
          attrGamesInterval: "Spiele-Intervall",
          attrGamesPlaceholder: "z.B. 6",
          attrGamesHelp: "Anzahl der Spiele zwischen jedem Attribut-Upgrade.",
          attrPoints: "Attributpunkte",
          attrPointsPlaceholder: "z.B. 4",
          attrPointsHelp: "Anzahl der Attributpunkte pro Intervall.",
          attrPreview: "Vorschau",
          attrPreviewText: "Alle {{games}} Spiele → {{points}} Attributpunkte",
          attributeUpgradesSaved: "Attribut-Upgrade Einstellungen gespeichert!",

          // Validation messages
          validation: {
            tradeLimitInvalid: "Trade-Limit muss eine gültige, nicht negative Zahl sein",
            labelRequired: "Bezeichnung ist erforderlich.",
            startDateRequired: "Startdatum ist erforderlich.",
            endDateRequired: "Enddatum ist erforderlich.",
            startBeforeEnd: "Startdatum darf nicht nach dem Enddatum liegen.",
            minGamesRequired: "Mindestanzahl Spiele ist erforderlich.",
            minGamesInvalid: "Mindestspiele muss eine gültige nicht-negative Zahl sein.",
            maxGamesInvalid: "Maximalspiele muss eine gültige nicht-negative Zahl sein.",
            maxGamesGteMin: "Maximalspiele muss größer oder gleich den Mindestspielen sein.",
            maxLtMinPlayoffs: "Maximale Saison-Spiele dürfen nicht kleiner sein als die Mindestspiele für Playoffs.",
            attrGamesInvalid: "Spiele-Intervall muss eine positive Zahl sein.",
            attrPointsInvalid: "Attributpunkte muss eine positive Zahl sein."
          }
        }
      },

      // Analytics / Daten & Statistiken
      analytics: {
        title: "Daten & Statistiken",
        subtitle: "Detaillierte Analyse der Liga-Leistungsdaten",
        currentSeason: "Aktuelle Saison",
        active: "Aktiv",
        noData: "Keine Daten verfügbar",
        backToAnalytics: "Zurück zur Analyse",
        backToRankings: "Zurück zu Rankings",
        cards: {
          teamRankings: { title: "Team-Rankings", desc: "Teams nach Siegen, Punkten und mehr vergleichen" },
          gmRankings: { title: "GM-Rankings", desc: "Wie schneiden die GMs in der Liga ab" },
          headToHead: { title: "Direktvergleich", desc: "Zwei Teams direkt vergleichen" },
          formTrends: { title: "Form & Trends", desc: "Team-Momentum und Scoring-Trends" },
          homeAway: { title: "Heim / Auswärts", desc: "Leistung nach Spielort aufgeschlüsselt" }
        },
        teamRankings: {
          title: "Team-Rankings",
          subtitle: "Komplette Teamübersicht mit erweiterten Statistiken"
        },
        gmRankings: {
          title: "GM-Rankings",
          subtitle: "General Manager Leistungsübersicht"
        },
        teamDetail: {
          title: "Team-Statistiken",
          rank: "Rang"
        },
        gmDetail: {
          title: "GM-Statistiken",
          rank: "Rang",
          team: "Team"
        },
        headToHead: {
          title: "Direktvergleich",
          subtitle: "Zwei Teams direkt vergleichen",
          team1: "Team 1",
          team2: "Team 2",
          selectTeam: "Team auswählen...",
          noGames: "Diese Teams haben noch nicht gegeneinander gespielt",
          totalGames: "Gesamtspiele",
          totalPoints: "Gesamtpunkte",
          comparison: "Vergleich",
          matchHistory: "Spielverlauf"
        },
        compliance: {
          title: "Compliance-Analyse",
          subtitle: "Datenqualitäts-Flags und Anomalieerkennung (nur Admin)",
          flaggedGames: "Markierte Spiele",
          showAll: "Alle anzeigen",
          noFlags: "Keine markierten Spiele gefunden — alles sauber!",
          flag: "Flag",
          matchup: "Begegnung",
          details: "Details",
          teamAnomalies: "Team-Anomalien",
          blowoutPct: "Blowout %",
          flags: {
            missing_stats: "Fehlende Stats",
            zeroed_stats: "Null-Stats",
            blowout: "Blowout",
            high_scoring: "Hohe Punktzahl"
          }
        },
        cols: {
          rank: "#",
          team: "Team",
          gm: "GM",
          gp: "SP",
          w: "S",
          l: "N",
          pct: "PCT",
          ppg: "PPG",
          oppg: "OPPG",
          diff: "DIFF",
          home: "Heim",
          away: "Ausw.",
          streak: "Serie",
          last5: "Letzte 5",
          last10: "Letzte 10",
          record: "Bilanz",
          date: "Datum",
          location: "Ort",
          opponent: "Gegner",
          result: "Ergebnis",
          score: "Punkte",
          scored: "Erzielt",
          allowed: "Kassiert"
        },
        tabs: {
          overview: "Übersicht",
          trends: "Trends",
          splits: "Splits",
          history: "Verlauf"
        },
        charts: {
          winPctOverTime: "Siegquote im Zeitverlauf",
          scoringTrend: "Scoring-Trend (5-Spiele-Schnitt)",
          diffTrend: "Punktedifferenz-Trend",
          gameResults: "Spielergebnisse",
          gameNumber: "Spiel #",
          game: "Spiel"
        },
        splits: {
          home: "Heim",
          away: "Auswärts",
          homeShort: "H",
          awayShort: "A"
        }
      },
      bench: {
        benchedSection: "Inaktiv ({{count}})",
        activeCount: "{{active}} aktiv · {{benched}} inaktiv",
        inactiveBadge: "Inaktiv",
        activeBadge: "Aktiv",
        deactivate: "✕ Deaktivieren",
        activate: "✓ Aktivieren",
        confirmDeactivate: "Diesen Spieler auf inaktiv setzen? Er bleibt im Kader, verschwindet aber von den öffentlichen Seiten und aus der Statistikerfassung.",
        confirmActivate: "Diesen Spieler wieder aktivieren?",
        updateFailed: "Status konnte nicht geändert werden",
        showBenched: "Inaktive Spieler anzeigen",
        benchedHint: "inaktiv",
        benchedAdminBadge: "Inaktiv (GM)"
      },

      // Kaderverwaltung im Admin-Bereich: GMs setzen Spieler auf inaktiv
      roster: {
        title: "Kader",
        intro: "Setze Spieler, die du nicht einsetzt, auf inaktiv. Sie bleiben im Kader, verschwinden aber von der öffentlichen Team-Seite, aus der Spielerliste und aus den Bestenlisten und tauchen bei der Statistikerfassung nicht mehr auf. Du kannst sie jederzeit zurückholen.",
        selectTeam: "Team",
        player: "Spieler",
        badges: "Badges",
        status: "Status",
        action: "Aktion",
        empty: "Keine Spieler in diesem Kader.",
        noTeam: "Dir ist kein Team zugeordnet. Wende dich an einen Admin.",
        loadFailed: "Kader konnte nicht geladen werden",
        overTarget: "mehr als {{target}} aktiv",
        benchedNote: "nur hier sichtbar",
        dismiss: "Schließen"
      },

      // Statistikerfassung: was der Namensabgleich Screenshot gegen Kader nicht aufloesen konnte
      ocrReview: {
        title: "Vor dem Absenden prüfen",
        intro: "Der Screenshot wurde korrekt gelesen, aber diese Zeilen ließen sich keinem Kaderspieler sicher zuordnen. Alles, was hier steht, wird nicht gespeichert — außer du trägst es unten von Hand nach.",
        unmatched: "Kein Spieler gefunden ({{count}})",
        unmatchedHint: "Zu diesem Namen passt kein Spieler im Kader. Die Zeile bitte unten von Hand nachtragen.",
        offRoster: "Nicht in diesem Kader ({{count}})",
        offRosterHint: "Einem Spieler aus einem anderen Team zugeordnet — meist ein Trade, der noch nicht eingetragen ist. Verworfen, weil die Zeile hier sonst unsichtbar wäre, aber trotzdem mit abgeschickt würde.",
        ambiguous: "Mehrdeutig ({{count}})",
        ambiguousHint: "Auf diesen Namen passen mehrere Spieler im Kader. Bitte die Zuordnung prüfen.",
        lineStats: "{{minutes}} MIN, {{points}} PKT",
        scoreCheckTitle: "Punkte gehen nicht auf",
        scoreMismatch: "{{team}}: die Statzeilen ergeben {{sum}}, im Endstand stehen {{score}} ({{diff}} fehlen)",
        scoreOk: "Punkte decken sich mit dem Endstand.",
        staleBuild: "Diese Seite liegt seit dem letzten Update offen und läuft noch auf einer alten Version. Bitte vor dem Einlesen neu laden (Cmd/Strg + R) — sonst können Spieler unbemerkt aus dem Boxscore fallen.",
        confirmMismatch: "Die Statzeilen ergeben nicht den Endstand:\n\n{{details}}\n\nMeist fehlt ein Spieler aus dem Screenshot. Trotzdem absenden?",
        confirmMismatchLine: "{{team}}: {{sum}} statt {{score}} ({{diff}} fehlen)",
        confirmNoStats: "Es ist keine einzige Statzeile erfasst.\n\nBeim Absenden werden die bereits gespeicherten Statistiken dieses Spiels gelöscht. Wenn du die Screenshots einlesen wolltest, brich ab und klicke zuerst auf \"Extract Stats\".\n\nTrotzdem absenden?"
      },
      finals: {
        notStarted: "Das Finale hat noch nicht begonnen."
      },

      // Spielervergleich (Head-to-head)
      compare: {
        title: "Spieler vergleichen",
        subtitle: "Head-to-head — Karriere-Stats, Attribute und Badges.",
        playerA: "Spieler A",
        playerB: "Spieler B",
        selectPlayer: "Spieler wählen",
        searchPlaceholder: "Spieler suchen…",
        freeAgent: "Free Agent",
        noResults: "Keine Spieler gefunden.",
        swap: "Tauschen",
        clear: "Entfernen",
        pickBoth: "Wähle zwei Spieler für den direkten Vergleich.",
        pickOther: "Wähle einen zweiten Spieler zum Vergleichen.",
        regularSeason: "Regular Season",
        playoffs: "Playoffs",
        statsTitle: "Karriere-Stats",
        attributesTitle: "Attribute",
        badgesTitle: "Badges",
        noStats: "Keine Stats erfasst.",
        noAttributes: "Noch keine Attributdaten.",
        noBadges: "Noch keine Badge-Daten.",
        overall: "Overall",
        potential: "Potenzial",
        totalAttributes: "Attribute gesamt",
        compareCta: "Vergleichen",
        comparePlayersCta: "Spieler vergleichen",
        tiers: {
          legendary: "Legendär",
          hof: "Hall of Fame",
          gold: "Gold",
          silver: "Silber",
          bronze: "Bronze",
          total: "Gesamt"
        },
        categories: {
          outsideScoring: "Wurf außen",
          insideScoring: "Abschluss innen",
          athleticism: "Athletik",
          playmaking: "Spielmacher",
          defense: "Verteidigung",
          rebounding: "Rebounds",
          intangibles: "Intangibles"
        }
      },

      // Trade-Budget pro Saison + Discord-Ankündigung
      trades: {
        budget: {
          line: "Saison-Budget: {{trades}}/{{maxTrades}} Trades · {{players}}/{{maxPlayers}} abgegebene Spieler",
          afterThisTrade: "→ mit diesem Trade: {{trades}} Trades / {{players}} Spieler",
          rule: "Maximal {{maxTrades}} Trades und {{maxPlayers}} abgegebene Spieler pro Team und Saison (offene Angebote zählen mit)",
          blockedTitle: "Saison-Trade-Budget überschritten",
          blockedTrades: "{{team}} hat alle {{max}} Trades dieser Saison schon genutzt ({{used}} belegt, offene Angebote eingerechnet).",
          blockedPlayers: "{{team}} würde {{adding}} weitere(n) Spieler abgeben und damit das Limit von {{max}} überschreiten ({{used}} bereits belegt).",
          serverBlocked: "Saison-Trade-Budget überschritten"
        },
        discord: {
          title: "Discord-Ankündigung",
          hint: "Deutscher Text für den Discord-Channel — kopieren und dort posten.",
          generate: "Text erzeugen",
          copy: "Kopieren",
          copied: "Kopiert!",
          copyFailed: "Text konnte nicht kopiert werden. Bitte manuell markieren.",
          createdWithText: "Trade-Angebot erstellt — der Discord-Text steht unten beim Angebot bereit."
        },
        // 3-Team-Trades
        multi: {
          addThirdTeam: "➕ Drittes Team hinzufügen",
          removeThirdTeam: "Drittes Team entfernen",
          thirdTeamLabel: "Drittes Team",
          selectTeam: "Team wählen...",
          threeTeamBadge: "3-Team-Trade",
          hint: "Bei einem 3-Team-Trade gibt jedes Team Spieler ab und erhält Spieler. Wähle für jeden ausgewählten Spieler das Zielteam.",
          playersFrom: "Spieler von {{team}} ({{count}}/{{max}})",
          targetTeam: "an",
          noTargetYet: "Ziel wählen",
          givesAway: "Gibt ab",
          receives: "Erhält",
          valid: "3-Team-Trade ist gültig",
          invalid: "3-Team-Trade ist nicht regelkonform",
          incomplete: "Jedes Team muss mindestens einen Spieler abgeben, und jeder ausgewählte Spieler braucht ein Zielteam.",
          ruleTotal: "Regel 5.2 (Gesamtsumme): Abgabe {{give}} OVR, Zugang {{receive}} OVR, Differenz {{difference}}",
          ruleAvg: "Regel 5.2.1 (Schnitt): Abgabe {{giveTotal}} ÷ {{giveCount}} = {{giveAvg}} OVR, Zugang {{receiveTotal}} ÷ {{receiveCount}} = {{receiveAvg}} OVR, Differenz {{difference}}",
          rule: "3-Team-Trades: jedes Team gibt ab und erhält, Regel 5.2 / 5.2.1 wird pro Team geprüft",
          approvalTitle: "Zustimmungen",
          approved: "zugestimmt",
          awaiting: "noch offen",
          alreadyApproved: "Schon zugestimmt",
          needsBothPartners: "Die Spieler wechseln, sobald beide Partner-Teams zugestimmt haben.",
          approvalRecorded: "Deine Zustimmung ist gespeichert — es fehlt noch das andere Partner-Team.",
          confirmAccept: "Diesen 3-Team-Trade annehmen? Die Spieler wechseln, sobald beide Partner-Teams zugestimmt haben."
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    
    // Language detection options
    detection: {
      // Order of language detection methods
      order: ['localStorage', 'navigator', 'querystring', 'cookie', 'htmlTag'],
      
      // Cache user language selection
      caches: ['localStorage'],
      
      // Keys for localStorage
      lookupLocalStorage: 'selectedLanguage',
    },
    
    // Fallback language if detection fails
    fallbackLng: 'de',
    lng: 'de', // Default language
    
    // Debug mode (disable in production)
    debug: process.env.NODE_ENV === 'development',

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Don't show keys for missing translations in development
    saveMissing: false,
    
    // Return empty string for missing keys instead of the key
    missingKeyHandler: (lng, ns, key) => {
      return '';
    }
  });

export default i18n;